import i18n from 'i18next'
import EmailObj, { EmailBodyPartInterface } from './EmailObj'
import { handleJmapCallError } from './jmapCallError'
import MailboxObj from './MailboxObj'
import type { MailboxData } from './MailboxObj'

import { safePreview } from 'libs/utils/safePreview'
import emailDB from 'libs/DB/emailDB'
import emailDetailDB from 'libs/DB/emailDetailDB'
import aiDataDB from 'libs/DB/aiDataDB'
import PostalMime from 'postal-mime'

import moment from 'moment'

import {
  jam,
  getJamClient,
  getEmailsByIds,
  download,
  defaultEmailProp,
  changeableEmailProp,
  detailEmailProp,
  emailBodyProp,
  requestThreadByMessageId,
  requestEmailIdsByMessageId,
  importEML,
} from 'libs/jamUtil'
import { requestWithAbortRetry } from 'pages/main/moveToEmailTarget'
import { buildEml, base64utf8String } from 'libs/eml-format'
import { getEnvParam, getTempEnvParam } from 'env'
import { ClientJS } from 'clientjs'
import { getDeviceUUID } from 'pUtils'

import { warningBase, showResult } from 'utils/ui'
// import { UIQueue } from 'utils/ui/UIQueue'
import { aiApiClient } from 'myNet'
import { generateFilename, getTextFromHTML } from 'utils'

import { clone } from 'lodash'

import attachmentHelper from 'pages/main/components/Attachment/helper'
import { getMeetingInfo } from 'pages/main/components/ThreadView/IcsParser'

import settings from 'popups/Settings'

import { store as reduxStore } from 'index'

import {
  updateMailboxes,
  deleteMailbox,
  requestEmailIds,
  requestThread,
  requestOneThread,
  requestUpdatedThreadQuery,
  jamUpdate,
  deleteEmails,
  deleteEmailsById,
  getCount,
} from 'libs/jamUtil'

import { MAILBOX_TYPE } from 'popups/Settings/def'

import { ACTION, TYPE, EMAIL_STATE, applyData, sortByReceivedAt } from './defConstants'
import dataManager from './DataManager'
import { countConsumedMatches } from './searchPagination'
import { buildThreadingHeaders } from './threadingHeaders'
import { buildReplyToHeaders, type ReplyToAddress } from './replyToHeader'
import type { ThreadingFields } from 'pages/main/components/SendHelper/threading'
import { addStaleThreadListDropBreadcrumb } from 'services/sentry'

import { useAiSummary } from 'env'
import {
  JmapSingleRequestLimit,
  useEmailDetailDB,
  emailSearchProvider,
  searchResultDisplayType,
} from 'vars'

// const aiSummaryQueue = new UIQueue({
//   name: 'aiApiQueue',
//   interval: 1000,
//   size: 1,
//   skipAnimationFrame: true,
// })

const checkEmailValid = (emailObj: any) => {
  if (emailObj == null || emailObj.data.messageId == null) {
    return false
  }
  return true
}

const sortBySize = (a, b) => {
  if (a == null || b == null) {
    return 0
  }

  if (checkEmailValid(a) === false || checkEmailValid(b) === false) {
    return 0
  }

  if (a.data.size < b.data.size) {
    return 1
  } else if (a.data.size > b.data.size) {
    return -1
  }
  return 0
}

const sortByFrom = (a, b) => {
  if (a == null || b == null) {
    return 0
  }

  if (checkEmailValid(a) === false || checkEmailValid(b) === false) {
    return 0
  }

  let aTxt = ''
  let bTxt = ''
  if (a.data.from != null && a.data.from.length > 0) {
    const aFrom = a.data.from[0]
    aTxt = aFrom.name || aFrom.email
    aTxt = aTxt.toLowerCase()
  }
  if (b.data.from != null && b.data.from.length > 0) {
    const bFrom = b.data.from[0]
    bTxt = bFrom.name || bFrom.email
    bTxt = bTxt.toLowerCase()
  }
  // return bTxt.compare(aTxt)
  if (aTxt < bTxt) {
    return 1
  } else if (aTxt > bTxt) {
    return -1
  }
  return 0
}

const createSortFunc = (sortInfo) => {
  if (sortInfo == null) {
    return sortByReceivedAt
  }
  if (sortInfo.type === 'sort_date') {
    if (sortInfo.direction === 'sort_asc') {
      return (a, b) => {
        if (a == null || b == null) {
          return 0
        }
        // if (checkEmailValid(a) === false || checkEmailValid(b) === false) {
        //   return 0
        // }
        return -1 * sortByReceivedAt(a, b)
      }
    } else {
      return (a, b) => {
        if (a == null || b == null) {
          return 0
        }
        // if (checkEmailValid(a) === false || checkEmailValid(b) === false) {
        //   return 0
        // }
        return sortByReceivedAt(a, b)
      }
    }
  } else if (sortInfo.type === 'sort_from') {
    if (sortInfo.direction === 'sort_asc') {
      return (a, b) => {
        return -1 * sortByFrom(a, b)
      }
    } else {
      return (a, b) => {
        return sortByFrom(a, b)
      }
    }
  } else if (sortInfo.type === 'sort_size') {
    if (sortInfo.direction === 'sort_asc') {
      return (a, b) => {
        return -1 * sortBySize(a, b)
      }
    } else {
      return (a, b) => {
        return sortBySize(a, b)
      }
    }
  }
}

// OFM-214: Email/get 로딩이 일시적 네트워크/API 에러로 실패해도 자동 복구되도록
// backoff 재시도를 적용한다. attempt는 0=최초, 재시도마다 +1.
const MAX_RETRY = 3
const BASE_DELAY = 1000
const MAX_BACKOFF = 8000

// reqDetail backoff 재시도 타이머를 모듈 수준에서 추적한다.
// emailObj별 타이머는 _detailRetryTimer에 있지만, clearJmapMailboxCaches(계정 전환)에서
// 모든 email 객체를 순회할 수 없으므로 여기서 일괄 정리한다.
const detailRetryTimers: Set<NodeJS.Timeout> = new Set()

interface SendData {
  emailObj: JMAPEmail
  mailboxObj: JMAPMailbox
  attempt: number
}

class JMAPReqQueue {
  private emailMap: Map<string, SendData> = new Map()
  private queue: SendData[] = []
  private isRunning: boolean = false
  private minInterval: number = 200 // 200ms 간격으로 요청.
  private lastCallTime: number = -1
  private maxRequestCount: number = 10

  private timer: NodeJS.Timeout | null = null

  // 재시도 backoff 타이머. clear() 시 일괄 정리하여 stale 재유입을 막는다.
  private retryTimers: Set<NodeJS.Timeout> = new Set()

  // 서버 복구 감지 리스너. Email/get 응답을 정상 수신하면(502 차단 해제 등) 발화한다.
  // 502 지속 장애는 navigator.onLine 전환이 없어 'online' 이벤트로 감지할 수 없으므로,
  // 실제 응답 성공을 "서버 복구" 신호로 삼아 stuck 항목/뷰어 본문 복구를 앞당긴다.
  private serverRecoveryListeners: Set<() => void> = new Set()

  // 직전에 실패(재시도 예약)가 있었는지. 정상 동작 중(실패 0)에는 성공 응답마다 복구
  // 리스너를 깨우지 않도록 하여 불필요한 재점검·재시도 상태 리셋을 막는다.
  private hadRecentFailure: boolean = false

  addServerRecoveryListener(cb: () => void) {
    this.serverRecoveryListeners.add(cb)
  }

  removeServerRecoveryListener(cb: () => void) {
    this.serverRecoveryListeners.delete(cb)
  }

  markFailure() {
    this.hadRecentFailure = true
  }

  notifyServerRecovery() {
    // 실패 이력이 없으면(정상 동작) 발화하지 않는다 → 매 응답마다의 오버헤드 제거 +
    // RELOAD_INTERVALS rate-limit가 선택적 실패에서도 유지되도록 한다.
    if (this.hadRecentFailure !== true) {
      return
    }
    this.hadRecentFailure = false
    this.serverRecoveryListeners.forEach((cb) => cb())
  }

  constructor(minInterval: number = 200, maxRequestCount: number = 10) {
    this.minInterval = minInterval
    this.maxRequestCount = maxRequestCount
    this.queue = []
  }

  // 실패한 로딩을 backoff 후 reqEmail로 재투입한다. 상한 초과/이미 로딩된 항목은 중단.
  private scheduleRetry(emailObj: JMAPEmail, mailboxObj: JMAPMailbox, attempt: number) {
    if (attempt > MAX_RETRY || emailObj.isLoaded === true) {
      return
    }
    // 실패 발생 표시: 이후 정상 응답 시 서버 복구 리스너를 깨운다.
    this.hadRecentFailure = true
    const delay = Math.min(BASE_DELAY * 2 ** (attempt - 1), MAX_BACKOFF)
    const timer = setTimeout(() => {
      this.retryTimers.delete(timer)
      if (emailObj.isLoaded === true) {
        return
      }
      this.reqEmail(emailObj, mailboxObj, attempt)
    }, delay)
    this.retryTimers.add(timer)
  }

  // 요청한 id가 응답에 누락되었거나 전송이 실패했을 때, 미로딩 항목만 재시도로 보낸다.
  private retryOrDrop(id: string) {
    const sendData = this.emailMap.get(id)
    this.emailMap.delete(id)
    if (sendData != null && sendData.emailObj.isLoaded === false) {
      this.scheduleRetry(sendData.emailObj, sendData.mailboxObj, sendData.attempt + 1)
    }
  }

  private requestEmails(ids: string[], onEnd: () => void) {
    if (ids.length > 0) {
      const client = getJamClient()
      if (client == null) {
        // 클라이언트 초기화 레이스: 즉시 포기하지 않고 backoff 재시도로 보낸다 (#3)
        ids.forEach((id) => this.retryOrDrop(id))
        onEnd?.()
        return
      }
      client.api.Email.get({
        accountId: jam.accountId,
        ids,
        properties: defaultEmailProp,
      })
        .then(([mailList]: any) => {
          // 정상 응답 수신 = 서버 복구 신호(차단 해제 등). stuck 항목/뷰어 본문 복구를 깨운다.
          this.notifyServerRecovery()
          const respondedIds = new Set<string>()
          if (mailList.list.length > 0) {
            // console.info(' --- reqEmail test - multiple response : ', mailList.list.length)
            mailList.list.forEach((item: any) => {
              const data = { ...item }
              const id = data.id
              respondedIds.add(id)
              const sendData = this.emailMap.get(id)
              // console.info(' --- reqEmail test 4 process response : ', id)
              if (sendData != null) {
                const { emailObj, mailboxObj } = sendData
                emailObj.setEmailData(mailboxObj, data, true)
              }
            })
          }
          // 응답에 누락된 id(서버 누락 등)는 미로딩이면 재시도, 응답된 id는 emailMap에서 정리 (#2)
          ids.forEach((id) => {
            if (respondedIds.has(id)) {
              this.emailMap.delete(id)
            } else {
              this.retryOrDrop(id)
            }
          })
          if (onEnd != null) {
            onEnd()
          }
        })
        .catch((err: any) => {
          // 전송 실패: 미로딩 항목을 backoff 재시도로 보낸다 (#1)
          console.log('requestEmails error', err)
          ids.forEach((id) => this.retryOrDrop(id))
          if (onEnd != null) {
            onEnd()
          }
        })
    }
  }

  reqEmail(emailObj: JMAPEmail, mailboxObj: JMAPMailbox, attempt: number = 0) {
    // in-flight dedupe: 이미 큐에 있거나 요청 중이면 중복 투입하지 않는다 (#A4)
    if (this.emailMap.has(emailObj.getId())) {
      return
    }

    if (JmapSingleRequestLimit > 0 && emailObj.data.receivedAt != null) {
      const receivedTime = new Date(emailObj.data.receivedAt).getTime()
      // console.info(
      //   ' --- reqEmail test 3 addQueue check : ',
      //   Date.now() - receivedTime,
      //   JmapSingleRequestLimit,
      // )

      if (Date.now() - receivedTime < JmapSingleRequestLimit) {
        const sendData: SendData = {
          emailObj,
          mailboxObj,
          attempt,
        }
        this.emailMap.set(emailObj.getId(), sendData)
        this.queue.push(sendData)
        // console.info(' --- reqEmail test 0 queue added : ', this.queue.length)
        if (this.queue.length >= this.maxRequestCount) {
          this.requestNow()
        } else {
          this.start()
        }
        return
      }
    }

    // 단건 경로: in-flight 가드로 동시 재호출의 중복 요청을 막고, 실패(reject)는 catch하여
    // backoff 재시도로 연결한다 (#5, #A5). emailMap을 쓰지 않는 경로의 dedupe 사각을 닫는다.
    if (emailObj._reqAllDataFlight != null) {
      return
    }
    emailObj._reqAllDataFlight = emailObj
      .reqAllData(mailboxObj)
      .catch(() => {
        this.scheduleRetry(emailObj, mailboxObj, attempt + 1)
      })
      .finally(() => {
        emailObj._reqAllDataFlight = null
      })
  }

  requestNow() {
    if (this.timer != null) {
      clearTimeout(this.timer)
    }
    this.timer = null
    this.isRunning = false

    if (this.queue.length > 0) {
      // console.info(' --- reqEmail test 1 requestNow : ', this.queue.length)
      const ids = this.queue.map((item) => item.emailObj.getId())
      this.queue.length = 0
      this.requestEmails(ids, null)
    }
  }

  start() {
    if (this.isRunning === true) {
      return
    }
    this.isRunning = true
    this.next()
  }

  next() {
    if (this.queue.length > 0) {
      const now = Date.now()
      if (this.lastCallTime > 0) {
        if (now - this.lastCallTime < this.minInterval) {
          const interval = this.minInterval - (now - this.lastCallTime)
          this.timer = setTimeout(() => {
            this.execute()
          }, interval)
        } else if (now - this.lastCallTime >= this.minInterval) {
          this.execute()
        }
      } else {
        this.timer = setTimeout(() => {
          this.execute()
        }, this.minInterval)
      }
    } else {
      this.isRunning = false
    }
  }

  execute() {
    if (this.queue.length > 0) {
      // console.info(' --- reqEmail test 2 request by timer : ', this.queue.length)

      const ids = this.queue.map((item) => item.emailObj.getId())
      this.queue.length = 0
      this.lastCallTime = Date.now()

      this.requestEmails(ids, () => {
        this.next()
      })
    }
  }

  clear() {
    if (this.timer != null) {
      clearTimeout(this.timer)
    }
    // 재시도 backoff 타이머를 모두 정리해 계정 전환/로그아웃 후 stale 재유입을 막는다.
    this.retryTimers.forEach((timer) => clearTimeout(timer))
    this.retryTimers.clear()
    this.queue = []
    this.isRunning = false
    this.timer = null
    this.emailMap.clear()
  }
}

export const jmapReqQueue = new JMAPReqQueue(100, 10)

const aiSummaryTargetMap = new Map<string, EmailObj[]>()

const aiSummaryMap = new Map<string, any>()

// 계정 전환/로그아웃(clearJmapMailboxCaches) 후에 발화하는 stale 비동기 콜백(요약 응답, code 900
// 재시도 타이머)이 비워진 캐시에 이전 계정 데이터를 재유입하지 않도록, 요청 시작 시점의 세대를
// 캡처해 generateData 진입 시 비교한다.
let aiSummaryGeneration = 0

export const clearJmapMailboxCaches = () => {
  aiSummaryGeneration += 1
  aiSummaryTargetMap.clear()
  aiSummaryMap.clear()
  jmapReqQueue.clear()
  // reqDetail backoff 재시도 타이머를 일괄 정리해 계정 전환 후 stale 발화를 막는다.
  detailRetryTimers.forEach((timer) => clearTimeout(timer))
  detailRetryTimers.clear()
}

const client: any = new ClientJS()

const getHeaders = async (language: string) => {
  const workspaceId = getTempEnvParam('app.user.workspace_id')
  const userEmail = getEnvParam('userEmail')
  const deviceUUID = await getDeviceUUID(userEmail)
  const headers = {
    'Output-Language': language || 'Korean',
    'Product-Name': 'Officemail-Web-Access',
    'User-Email': userEmail,
    Platform: client.getOS(),
    DeviceUuid: deviceUUID,
    'Workspace-ID': workspaceId,
  }

  return headers
}

export const getMessageId = (msgId: string) => {
  let messageId = msgId
  if (messageId.startsWith('<') !== true) {
    messageId = `<${messageId}`
  }
  if (messageId.endsWith('>') !== true) {
    messageId = `${messageId}>`
  }
  return messageId
}

export const getAiSummaryLimitTime = () => {
  const services = getTempEnvParam('app.services')
  // app.services 는 로그아웃(강제 로그아웃 포함) 정리 시 제거될 수 있어 undefined 방어.
  if (services?.ai != null && services.ai.summary_range != null) {
    const oneDay = 24 * 60 * 60 * 1000
    const timeLimit = Date.now() - oneDay * services.ai.summary_range
    return timeLimit
  }
  return -1
}

const createAiSummary = async (
  messageId: string,
  language: string,
  emailObj: EmailObj,
  forced: boolean = false,
) => {
  const headers = await getHeaders(language)

  const detail = await emailObj.reqDetail()
  const { from, to, cc, subject, bodyPart } = detail

  const email_from = from != null && from[0].email != null ? from[0].email : null
  const email_to = (to || [])
    .map((item) => {
      return item.email
    })
    .filter((item) => {
      return item != null
    })
  const email_cc = (cc || [])
    .map((item) => {
      return item.email
    })
    .filter((item) => {
      return item != null
    })
  const email_subject = subject != null ? subject : null

  let email_body = ''
  if (bodyPart.hasHTMLBody() === true) {
    const htmlText = bodyPart.getHTMLBody()
    email_body = getTextFromHTML(htmlText)
  } else if (bodyPart.hasTextBody() === true) {
    email_body = bodyPart.getTextBody()
  }

  const data = {
    message_id: messageId,
    email_body,
    email_from,
    email_to,
    email_cc,
    email_subject,
    is_forced: forced === true,
  }

  const res = await aiApiClient.post('/summary', data, { headers })

  if (res.data.code === 200) {
    return res.data.data
  } else {
    return null
  }
}

const setAILoading = (messageId: string, emailObj: EmailObj, loading: boolean | null) => {
  const emailObjs = aiSummaryTargetMap.get(messageId)
  let target = null
  const aiSummaryLimitTime = getAiSummaryLimitTime()
  if (emailObjs != null) {
    emailObjs.forEach((item) => {
      const receivedTime = new Date(item.data.receivedAt).getTime()
      if (loading != null && aiSummaryLimitTime > 0 && receivedTime > aiSummaryLimitTime) {
        item.extData.aiLoading = loading
      }
      item.reactMgr.updateUI()
    })
    target = [...emailObjs]
  } else {
    const receivedTime = new Date(emailObj.data.receivedAt).getTime()
    if (loading != null && aiSummaryLimitTime > 0 && receivedTime > aiSummaryLimitTime) {
      emailObj.extData.aiLoading = loading
    }
    emailObj.reactMgr.updateUI()
    target = [emailObj]
  }

  if (loading !== true) {
    // aiSummary loading이 완료되면 호출.
    emailObj.dataMgr.emailChangeListener.notify({
      target: dataManager,
      targetType: TYPE.DATAMANAGER,
      action: ACTION.UPDATE,
      type: TYPE.EMAIL,
      data: {
        messageId,
        list: target,
      },
    })
  }
}

const getSummary = (messageId: string, emailObj: EmailObj, headers: any) => {
  setAILoading(messageId, emailObj, true)
  return aiApiClient.get('/summary', { message_id: messageId }, { headers })
}

const generateData = (
  messageId: string,
  emailObj: EmailObj,
  resData: any,
  reqLanguage: string,
  headers: any,
  generation: number,
) => {
  if (generation !== aiSummaryGeneration) {
    // 계정 전환으로 캐시가 비워진 이후 도착한 stale 응답/재시도 — 재유입 차단.
    return null
  }

  const { code, message, data, reloadTime } = resData

  let summaryData: any = {
    new_summary: {},
    detailed_summary: {},
    concise_summary: {},
    new_title: {},
  }

  let needShowLoading = false

  if (code === 200) {
    //정상처리.
    // const { new_summary, detailed_summary, concise_summary, new_title } = data.result;

    if (aiSummaryMap.has(messageId)) {
      summaryData = aiSummaryMap.get(messageId)
    }

    Object.keys(summaryData).forEach((key) => {
      if (data != null && data.result[key] != null) {
        data.result[key].forEach((item: any) => {
          const { language, translation } = item
          summaryData[key][language] = {
            code: 200,
            translation,
          }
        })
      }
    })
  } else if (code === 900) {
    if (aiSummaryMap.has(messageId)) {
      summaryData = aiSummaryMap.get(messageId)
    }

    let maxLodingTime = null

    Object.keys(summaryData).forEach((key) => {
      summaryData[key][reqLanguage] = {
        ...(summaryData[key][reqLanguage] || {}),
        code,
        message,
      }
      if (reloadTime != null) {
        summaryData[key][reqLanguage].reloadTime = reloadTime
      }

      if (summaryData[key][reqLanguage].reloadTime != null) {
        maxLodingTime = summaryData[key][reqLanguage].reloadTime
      }
    })

    if (maxLodingTime != null) {
      const curTime = Date.now()
      const remainTime = maxLodingTime - curTime
      const limitTime = 1000 // 1초 간격으로 요청.
      let interval = 0
      if (remainTime > limitTime) {
        interval = limitTime
      } else if (remainTime > 0) {
        interval = remainTime
      }
      if (interval > 0) {
        // 일정 시간 이후 다시 요청.
        setTimeout(() => {
          getSummary(messageId, emailObj, headers)
            .then((res) => {
              const { data } = res
              generateData(messageId, emailObj, data, reqLanguage, headers, generation)
            })
            .catch(() => {
              if (generation !== aiSummaryGeneration) {
                return
              }
              setAILoading(messageId, emailObj, false)
              aiSummaryTargetMap.delete(messageId)
            })
        }, interval) // 일정 시간 후 다시 요청.
        needShowLoading = true
      }
    }
  } else {
    // 404등.
    // 오류 처리.

    if (aiSummaryMap.has(messageId) === false) {
      const aiSummaryLimitTime = getAiSummaryLimitTime()
      if (aiSummaryLimitTime > 0) {
        // AI 데이터가 없는 경우, services에서 정의된 기간 내에 받은 메일은 다시 생성하도록 처리.
        const receivedTime = new Date(emailObj.data.receivedAt).getTime()
        if (receivedTime > aiSummaryLimitTime && emailObj instanceof JMAPEmail) {
          setTimeout(() => {
            // 50ms 사이 계정 전환(clearJmapMailboxCaches)이 일어났다면 stale emailObj로
            // 재요청해 이전 계정 데이터를 재유입시키지 않도록 generation을 재확인한다. (R11)
            if (generation !== aiSummaryGeneration) {
              return
            }
            emailObj.reloadAiSummary(false).catch((err: Error) => {
              console.error('[AI Summary] reloadAiSummary error:', err.message)
            })
          }, 50)
        }
      }
    }

    Object.keys(summaryData).forEach((key) => {
      summaryData[key][reqLanguage] = {
        code,
        message,
      }
    })
  }

  aiSummaryMap.set(messageId, summaryData)

  try {
    aiDataDB.putData(messageId, summaryData)
  } catch (error) {
    console.error('aiDataDB putData error', error)
  }

  setAILoading(messageId, emailObj, needShowLoading)

  if (needShowLoading === false) {
    // 요청 수명주기 종료(성공/실패 확정) — UI 갱신용 EmailObj 참조를 해제해 GC를 막지 않는다.
    // code 900 재시도 중에는 항목을 유지해 재시도 완료 시 UI 갱신이 이어지게 한다.
    aiSummaryTargetMap.delete(messageId)
  }

  return summaryData
}

const addEmailObjByMessageId = (messageId: string, emailObj: EmailObj): boolean => {
  if (aiSummaryTargetMap.has(messageId)) {
    const emailObjs = aiSummaryTargetMap.get(messageId)
    if (
      emailObjs.find((item) => {
        // 중복되지 않도록 처리.
        return item.getId() === emailObj.getId()
      }) == null
    ) {
      emailObjs.push(emailObj)
    }
    return false
  } else {
    aiSummaryTargetMap.set(messageId, [emailObj])
  }
  return true
}

const reqAiSummary = async (
  messageId: string,
  language: string,
  emailObj: EmailObj,
  forced: boolean = false,
) => {
  const isNew = addEmailObjByMessageId(messageId, emailObj)
  if (forced !== true && isNew === false) {
    return Promise.resolve(null)
  }

  const generation = aiSummaryGeneration
  const headers = await getHeaders(language)

  getSummary(messageId, emailObj, headers)
    .then((res) => {
      const { data } = res
      generateData(messageId, emailObj, data, language, headers, generation)
    })
    .catch(() => {
      if (generation !== aiSummaryGeneration) {
        return
      }
      // 요약 요청 실패 — 로딩 해제 + targetMap 잔류 차단.
      setAILoading(messageId, emailObj, false)
      aiSummaryTargetMap.delete(messageId)
    })

  // const { queue } = aiSummaryQueue
  // let qData = null

  // if (queue.length > 0) {
  //   console.log('--- queue : ', queue)
  //   qData = queue[0]
  //   if (qData.ids.indexOf(messageId) < 0) {
  //     qData.ids = [...qData.ids, messageId]
  //   }
  // } else {
  //   qData = {
  //     ids: [messageId],
  //     run: (curData: any) => {
  //       console.log('--- run queue : ', queue)
  //       getHeaders().then((headers) => {
  //         aiApiClient
  //           .get(
  //             '/summaries',
  //             {
  //               message_ids: curData.ids.join(' '),
  //             },
  //             { headers },
  //           )
  //           .then((res) => {
  //             console.log('--- reqAiSummary res : ', res)
  //           })
  //           .catch((err) => {
  //             console.log('--- reqAiSummary err : ', err)
  //           })
  //       })
  //     },
  //   }
  //   aiSummaryQueue.push(qData)
  // }
}

export const createAiSummaryForNew = async (emailObj: EmailObj, htmlText: string) => {
  if (useAiSummary() !== true) {
    return null
  }

  const { messageId } = emailObj.data

  if (messageId == null || messageId.length === 0) {
    return null
  }

  const msgId = getMessageId(emailObj.data.messageId[0])

  const isNew = addEmailObjByMessageId(msgId, emailObj)
  if (isNew === false) {
    return null
  }

  // const aiData = aiSummaryMap.get(msgId)
  // if (aiData != null) {
  //   return Promise.resolve(aiData);    return
  // }

  const generation = aiSummaryGeneration
  const aiSettings = settings.getValue('ai')

  // generateData(msgId, emailObj, {
  //   code: 900,
  //   message: 'AI summary is waiting for creating...',
  // }, aiSettings.language, null)

  if (useAiSummary() === true) {
    const headers = await getHeaders(aiSettings.language)

    let detail: any
    try {
      detail = await emailObj.reqDetail()
    } catch (err) {
      console.error('[JMAPMailbox] createAiSummaryForNew reqDetail failed', err)
      return null
    }
    const { from, to, cc, subject } = detail

    const email_from = from != null && from[0].email != null ? from[0].email : null
    const email_to = (to || [])
      .map((item) => {
        return item.email
      })
      .filter((item) => {
        return item != null
      })
    const email_cc = (cc || [])
      .map((item) => {
        return item.email
      })
      .filter((item) => {
        return item != null
      })
    const email_subject = subject != null ? subject : null

    const email_body = htmlText

    const data = {
      message_id: msgId,
      email_body,
      email_from,
      email_to,
      email_cc,
      email_subject,
      is_forced: false,
    }

    try {
      await aiApiClient.post('/summary', data, { headers })

      generateData(
        msgId,
        emailObj,
        {
          code: 900,
          message: 'AI summary is creating...',
          reloadTime: Date.now() + 10000, // 10초 동안 다시 로딩 시도.
        },
        aiSettings.language,
        headers,
        generation,
      )
    } catch (error) {
      console.error('createAiSummaryForNew error', error)
      if (generation === aiSummaryGeneration) {
        // 생성 요청 실패 — addEmailObjByMessageId로 등록한 targetMap 항목 잔류 차단.
        setAILoading(msgId, emailObj, false)
        aiSummaryTargetMap.delete(msgId)
      }
    }
  }

  return null
}

export interface JMAPEmailInterface {
  // JMAPEmail에서 구현되는 함수들을 abstract로 선언
  applyData(data: any, notiInfo: any, isRemove: any, runSort: any, mailboxInfos: any): any
  applyRemove(emailData: any, notiInfo: any, mailboxInfos: any): any
  makeBodyPartFromJMAP(detail: any): Promise<EmailBodyPartInterface>
  reloadAiSummary(): Promise<any>
}

export interface JMAPMailboxContainer {
  notifyLastStateChanged: (lastState: string) => void
}

// 1. JMAPInterface 정의 (필요한 함수만 any 타입으로 선언)
export interface JMAPMailboxInterface {
  applyData: (data: any) => any
  addEmailForEmailType: (emailId: any) => void
  removeEmailForEmailType: (emailId: any) => void
  getEmailIdx: (email: any) => number
  getThreadIdx: (thread: any) => number
  addEmailData: (emailData: any, notiInfo: any, runSort: any) => any
  removeEmailData: (emailData: any, notiInfo: any, removeFromThread?: any) => any
  addThreadData: (threadData: any, notiInfo: any, runSort: any) => any
  addEmail_no_noti: (email: any, runSort: any) => any
  removeEmail_no_noti: (email: any) => any
  addThread_no_noti: (thread: any, runSort: any) => any
  removeThread_no_noti: (thread: any) => any
  isValidThread: (thread: any) => boolean
  addDelayedData?: (emails: any) => void
  clearDelayedData?: () => void
  setKeywordByEmailIds?: (emailIds: string[], subkey: string, val: boolean) => Promise<any>
  markAllasRead?: () => Promise<any>
  importMessage?: (
    emlFiles: File[],
    onProgress: (progress: number, count: number, total: number) => void,
  ) => Promise<any>
  applyEmailDeletedFromServer?: (emailIdList: any, notiInfo: any) => void
  reqOneThread?: (emailId: any) => any
  getSortFunc: () => any
  sortEmail?: () => void
  sortThread?: () => void
  checkRelatedMailboxes?: (emailObj: any, notiInfo: any) => void
  move?: (messages: any, toMailBox: any, isRestore: any, useResultUI: any) => any
  archive?: (messages: any, useResultUI: any) => any
  getJmapFilter: () => any
  // ... 필요시 추가 ...
}

export class JMAPEmail extends EmailObj implements JMAPEmailInterface {
  detail: any
  importance: any
  dnObj: any
  viewSubject: string
  nonInlineAttachments: any[]
  selfLoadingList: any[]
  // reqAllData 단건 로딩 in-flight 가드: 진행 중 동시 재호출의 중복 요청을 막는다.
  _reqAllDataFlight: Promise<unknown> | null = null
  // reqDetail in-flight 가드: 같은 메일의 동시 reqDetail 호출 시 단일 Email/get으로 수렴.
  _detailFlight: Promise<any> | null = null
  // reqDetail 네트워크 에러 backoff 재시도 타이머. 다른 메일 선택/계정 전환 시 정리.
  _detailRetryTimer: NodeJS.Timeout | null = null

  constructor(dataMgr: any, id: any) {
    super(dataMgr)

    this.data = {
      id,
      mailboxIds: {},
    }

    this.detail = null
    this.importance = null
    this.dnObj = undefined
    this.viewSubject = null
    this.nonInlineAttachments = []
    this.isLoaded = false
  }

  checkAiSummary = async (data: any, language: string) => {
    const { messageId } = data

    if (messageId == null || messageId.length === 0) {
      return null
    }

    // ai-summary를 요청해야 하는 조건을 정의하자.
    // 1. ai-summary가 없는 경우
    const msgId = getMessageId(messageId[0])

    try {
      let aiData = aiSummaryMap.get(msgId)
      if (aiData != null) {
        return aiData
      }

      aiData = await aiDataDB.getData(msgId)
      if (aiData != null) {
        const { data } = aiData
        aiSummaryMap.set(msgId, data)
        return data
      }
    } catch (error: any) {
      console.log('aiDataDB.getData error', error)
    }

    // ai-summary를 요청시에는 "<message-id>" 형식으로 요청해야 한다.
    const ret = await reqAiSummary(msgId, language, this)

    // 응답을 받으면 무조건 저장해야 함.
    // code가 900인 경우에만 일정 시간 이후 다시 요청해야 함.
    return ret

    // const headers = {
    //   'Output-Language': 'Korean',
    // };
    // aiApiClient.get('/summary', {
    //   message_id: msgId,
    // }, { headers }).then((res) => {
    //   console.log('--- reqAiSummary res : ', res);
    // }).catch((err) => {
    //   console.log('--- reqAiSummary err : ', err);
    // })
  }

  reloadAiSummary = async (forced: boolean = true) => {
    const { messageId } = this.data

    if (messageId == null || messageId.length === 0) {
      return null
    }

    if (useAiSummary() === true && this.data.messageId != null) {
      const aiSettings = settings.getValue('ai')
      const msgId = getMessageId(this.data.messageId[0])
      const aiData = aiSummaryMap.get(msgId)
      let needCreate = false
      if (aiData != null) {
        const { concise_summary } = aiData || {}
        const aiSummary = concise_summary[aiSettings.language]
        if (aiSummary == null || (aiSummary.code !== 200 && aiSummary.code !== 900)) {
          // 데이터가 없거나 로딩 중인 경우에는, 다시 생성처리.
          needCreate = true
        }
      } else {
        needCreate = true
      }

      if (needCreate === true) {
        const generation = aiSummaryGeneration
        try {
          await createAiSummary(msgId, aiSettings.language, this, forced)
        } catch (err) {
          console.error('[JMAPMailbox] reloadAiSummary createAiSummary failed', err)
          return null
        }

        const headers = await getHeaders(aiSettings.language)
        generateData(
          msgId,
          this,
          {
            code: 900,
            message: 'AI summary is creating...',
            reloadTime: Date.now() + 10000, // 10초 동안 다시 로딩 시도.
          },
          aiSettings.language,
          headers,
          generation,
        )
      } else {
        // 다시 로딩
        const ret = await reqAiSummary(msgId, aiSettings.language, this, true) // 강제로 다시 로딩.
        return ret

        // const aiData0 = aiSummaryMap.get(msgId)
        // if (aiData0 != null) {
        //   const { concise_summary } = aiData0 || {}
        //   const aiSummary = concise_summary[aiSettings.language]
        //   if (aiSummary != null) {
        //     aiSummary.translation = aiSummary.translation + ' 1'

        //     setAILoading(msgId, this, null);
        //   }
        // }
        // return null
      }
    }

    return null
  }

  setEmailData = (mailboxObj: any, data: any, addDB: boolean) => {
    if (addDB === true) {
      emailDB.putData(data.id, data)

      if (this.isDraft() === false) {
        const { from } = data

        if (Array.isArray(from) && from[0] != null) {
          this.dataMgr.addTempEmail(from[0])
        }
        if (Array.isArray(data.to)) {
          data.to.forEach((to: any) => {
            if (to != null) {
              this.dataMgr.addTempEmail(to)
            }
          })
        }
        if (Array.isArray(data.cc)) {
          data.cc.forEach((cc: any) => {
            if (cc != null) {
              this.dataMgr.addTempEmail(cc)
            }
          })
        }
        if (Array.isArray(data.bcc)) {
          data.bcc.forEach((bcc: any) => {
            if (bcc != null) {
              this.dataMgr.addTempEmail(bcc)
            }
          })
        }
      }
    }

    if (useAiSummary() === true) {
      const aiSettings = settings.getValue('ai')
      // 이 부분은 비동기 처리가 되어야 함. (결과를 받았을 때 화면 갱신 처리.)
      this.checkAiSummary(data, aiSettings.language).then(() => {
        this.reactMgr.updateUI()
      })
    }

    const notiInfo = {}

    // 검색 세션에서 이동/삭제(movedEmailIds)된 메일의 늦은 로딩 응답(fake 2단계 로딩,
    // emailDB 캐시 등)이 검색 목록에 재추가되지 않도록, 목록 추가 없이 전역 데이터만 갱신한다.
    // 재검색 응답은 reqNextThreadList의 병합 필터가 막지만 이 경로는 필터를 거치지 않는다.
    let targetMailboxObj = mailboxObj
    if (
      targetMailboxObj != null &&
      typeof targetMailboxObj.isMovedEmail === 'function' &&
      targetMailboxObj.isMovedEmail(data.id) === true
    ) {
      targetMailboxObj = null
    }

    if (targetMailboxObj != null) {
      targetMailboxObj.addEmailData(data, notiInfo, false)
    } else {
      this.dataMgr.applyEmailData(data, notiInfo, true, null)
    }

    if (notiInfo != null) {
      const eventList = this.dataMgr.makeEvent0(notiInfo)
      const notiEvent = eventList.filter((item: any) => {
        return item.type === TYPE.THREAD || item.type === TYPE.EMAIL
      })
      const notiMap: any = {}
      notiEvent.forEach((event: any) => {
        if (event.targetType === TYPE.MAILBOX) {
          const mailboxId = event.target.getId()
          if (notiMap[mailboxId] == null) {
            notiMap[mailboxId] = []
          }
          notiMap[mailboxId].push(event)

          if (event.type === TYPE.EMAIL) {
            this.dataMgr.emailChangeListener.notify(event)
          } else if (event.type === TYPE.THREAD) {
            this.dataMgr.notifyThreadChanged([event])
          }
        }
      })
      Object.entries(notiMap).forEach(([mailboxId, notiList]: any) => {
        const mailbox = this.dataMgr.getMailboxById(mailboxId)
        mailbox.threadChangeListener.notify(notiList)
      })
    }
  }

  getEmailDataFromDB = (emailData: any, mailboxObj: any) => {
    // this.reqAllData(mailboxObj);
    const { id } = emailData
    emailDB
      .getData(id)
      .then((dbData) => {
        // console.log('getEmailDataFromDB', id, dbData)
        if (dbData != null) {
          const data = {
            ...dbData.data,
            ...emailData,
          }
          this.setEmailData(mailboxObj, data, false)
        } else {
          // 일치하는 데이터가 없음.
          // 묶어서 요청하도록 수정.
          jmapReqQueue.reqEmail(this, mailboxObj)
        }
      })
      .catch((error) => {
        // IndexedDB 읽기 실패 시에도 서버에서 직접 로딩을 시도한다 (#4)
        console.error('getEmailDataFromDB error', error)
        jmapReqQueue.reqEmail(this, mailboxObj)
      })
  }

  applyData = (data: any, notiInfo: any, isRemove: any, runSort: any, mailboxInfos: any) => {
    const targetData = this.data

    let isFake = false
    let isOpenSearch = false
    if (data.extra != null) {
      isFake = data.extra.isFake
      isOpenSearch = data.extra.isOpenSearch === true
      if (isFake === true) {
        if (isOpenSearch === true) {
          // OFM-225: 검색 결과는 emailId만 신뢰한다. 이미 로드된 메일은 재조회가 불필요하고,
          // 미로드 메일은 id만 넘겨 검색 유래 필드(단일 mailboxIds 등)가 emailDB 캐시
          // 실데이터를 덮어쓰지({...dbData.data, ...data} 병합) 않게 한다.
          if (this.isLoaded !== true) {
            this.getEmailDataFromDB({ id: data.id }, data.extra.mailboxObj)
          }
        } else {
          this.getEmailDataFromDB(data, data.extra.mailboxObj)
        }
      }
      delete data.extra
    }

    // 이미 JMAP 실데이터를 가진 캐시 메일(isLoaded===true)은 그 데이터가 권위를 가진다.
    // OpenSearch 검색 결과는 읽음/플래그/첨부/스레드 정보가 없어 검색용 기본값(keywords={},
    // hasAttachment=false, attachments=[], threadId=합성, from 단일→배열 축소 등)으로 채워지고,
    // 표시 필드(subject/preview/from/to/cc/receivedAt)도 인덱스 유래라 JMAP보다 불완전할 수 있다.
    // 이 값이 공유 캐시 EmailObj에 병합되면 받은편지함/보낸편지함에 누수된다(읽음 오표시·스레드
    // 붕괴·메일함 소속 소실·수신자 축소·정렬 뒤틀림). 따라서 캐시 메일에는 검색 데이터의 어떤
    // 필드도 병합하지 않고, 메일함 소속만 union하여 검색 목록에는 나타나되 실제 소속은 보존한다.
    // 검색으로 처음 보는 메일(isLoaded !== true)은 기존대로 검색 기본값을 사용한다.
    const preserveOpenSearch = isOpenSearch === true && this.isLoaded === true
    if (preserveOpenSearch === true) {
      Object.keys(data).forEach((key) => {
        if (key !== 'id' && key !== 'mailboxIds') {
          delete data[key]
        }
      })
      if (data.mailboxIds != null) {
        data.mailboxIds = { ...targetData.mailboxIds, ...data.mailboxIds }
      } else {
        delete data.mailboxIds
      }
    }

    const isNew = this.isNew

    if (isFake !== true && preserveOpenSearch !== true) {
      this.calcFixedData(data)

      const importance = data['header:Importance']
      if (importance != null) {
        this.importance = importance.trim().toLowerCase()
      } else {
        const { headers } = data
        if (headers != null) {
          const importanceHeader = headers.find(
            (header: any) => header.name.toLowerCase() === 'importance',
          )
          if (importanceHeader != null && importanceHeader.value != null) {
            this.importance = importanceHeader.value.trim().toLowerCase()
          }
        }
      }
    }

    let deleteMailboxIdList: any[] = []
    let addMailboxIdList: any[] = []
    let updateMailboxIdList: any[] = []
    if (mailboxInfos == null || mailboxInfos.length === 0) {
      if (isRemove !== true) {
        const oldMailboxIds = targetData.mailboxIds || {}
        const newMailboxIds = data.mailboxIds || {}
        deleteMailboxIdList = [...Object.keys(oldMailboxIds)]
        addMailboxIdList = []
        updateMailboxIdList = []
        Object.keys(newMailboxIds).forEach((mailboxId) => {
          if (oldMailboxIds[mailboxId] === true) {
            updateMailboxIdList.push(mailboxId)
            const idx = deleteMailboxIdList.indexOf(mailboxId)
            if (idx > -1) {
              deleteMailboxIdList.splice(idx, 1)
            }
          } else {
            addMailboxIdList.push(mailboxId)
          }
        })
      } else if (data != null && data.mailboxIds != null) {
        deleteMailboxIdList = [...Object.keys(data.mailboxIds)]
        addMailboxIdList = []
        updateMailboxIdList = []
      }
    } else {
      mailboxInfos.forEach((mailboxInfo: any) => {
        const { mailbox, action } = mailboxInfo
        if (action === ACTION.ADD) {
          addMailboxIdList.push(mailbox.getId())
        } else if (action === ACTION.DELETE) {
          deleteMailboxIdList.push(mailbox.getId())
        } else if (action === ACTION.UPDATE) {
          updateMailboxIdList.push(mailbox.getId())
        }
      })
    }
    const isChanged = applyData(data, targetData)
    this.isLoaded = true
    const mailboxIdMap: any = {}
    addMailboxIdList.forEach((mailboxId) => {
      const mailbox = this.dataMgr.getMailboxById(mailboxId)
      mailbox.addEmail_no_noti(this, false)
      mailboxIdMap[mailboxId] = true
    })
    deleteMailboxIdList.forEach((mailboxId) => {
      const mailbox = this.dataMgr.getMailboxById(mailboxId)
      mailbox.removeEmail_no_noti(this, false)
    })
    updateMailboxIdList.forEach((mailboxId) => {
      const mailbox = this.dataMgr.getMailboxById(mailboxId)
      mailbox.addEmail_no_noti(this, false)
      mailboxIdMap[mailboxId] = true
    })
    if (runSort !== false) {
      Object.keys(mailboxIdMap).forEach((mailboxId) => {
        const mailbox = this.dataMgr.getMailboxById(mailboxId)
        mailbox.sortEmail()
      })
    }
    if (notiInfo != null) {
      if (addMailboxIdList.length > 0 || deleteMailboxIdList.length > 0) {
        addMailboxIdList.forEach((mailboxId) => {
          this.dataMgr.removeEvent0Obj(
            notiInfo,
            TYPE.EMAIL,
            ACTION.DELETE,
            { type: TYPE.MAILBOX, id: mailboxId },
            this,
          )
          if (
            this.dataMgr.isExistEvent0Obj(
              notiInfo,
              TYPE.EMAIL,
              ACTION.UPDATE,
              { type: TYPE.MAILBOX, id: mailboxId },
              this,
            ) === false
          ) {
            this.dataMgr.addEvent0Obj(
              notiInfo,
              TYPE.EMAIL,
              ACTION.ADD,
              { type: TYPE.MAILBOX, id: mailboxId },
              this,
            )
          }
        })
        deleteMailboxIdList.forEach((mailboxId) => {
          this.dataMgr.removeEvent0Obj(
            notiInfo,
            TYPE.EMAIL,
            ACTION.ADD,
            { type: TYPE.MAILBOX, id: mailboxId },
            this,
          )
          this.dataMgr.removeEvent0Obj(
            notiInfo,
            TYPE.EMAIL,
            ACTION.UPDATE,
            { type: TYPE.MAILBOX, id: mailboxId },
            this,
          )
          this.dataMgr.addEvent0Obj(
            notiInfo,
            TYPE.EMAIL,
            ACTION.DELETE,
            { type: TYPE.MAILBOX, id: mailboxId },
            this,
          )
        })
        updateMailboxIdList.forEach((mailboxId) => {
          this.dataMgr.removeEvent0Obj(
            notiInfo,
            TYPE.EMAIL,
            ACTION.DELETE,
            { type: TYPE.MAILBOX, id: mailboxId },
            this,
          )
          if (
            this.dataMgr.isExistEvent0Obj(
              notiInfo,
              TYPE.EMAIL,
              ACTION.ADD,
              { type: TYPE.MAILBOX, id: mailboxId },
              this,
            ) === false
          ) {
            this.dataMgr.addEvent0Obj(
              notiInfo,
              TYPE.EMAIL,
              ACTION.UPDATE,
              { type: TYPE.MAILBOX, id: mailboxId },
              this,
            )
          }
        })
        updateMailboxIdList.length = 0
      }
      if (isRemove !== true) {
        if (isChanged === true) {
          updateMailboxIdList.forEach((mailboxId) => {
            this.dataMgr.removeEvent0Obj(
              notiInfo,
              TYPE.EMAIL,
              ACTION.DELETE,
              { type: TYPE.MAILBOX, id: mailboxId },
              this,
            )
            if (
              this.dataMgr.isExistEvent0Obj(
                notiInfo,
                TYPE.EMAIL,
                ACTION.ADD,
                { type: TYPE.MAILBOX, id: mailboxId },
                this,
              ) === false
            ) {
              this.dataMgr.addEvent0Obj(
                notiInfo,
                TYPE.EMAIL,
                ACTION.UPDATE,
                { type: TYPE.MAILBOX, id: mailboxId },
                this,
              )
            }
          })
          this.dataMgr.removeEvent0Obj(
            notiInfo,
            TYPE.EMAIL,
            ACTION.DESTROY,
            { type: TYPE.DATAMANAGER, id: 'all' },
            this,
          )
          if (isNew === true) {
            if (
              this.dataMgr.isExistEvent0Obj(
                notiInfo,
                TYPE.EMAIL,
                ACTION.MODIFY,
                { type: TYPE.DATAMANAGER, id: 'all' },
                this,
              ) === false
            ) {
              this.dataMgr.addEvent0Obj(
                notiInfo,
                TYPE.EMAIL,
                ACTION.CREATE,
                { type: TYPE.DATAMANAGER, id: 'all' },
                this,
              )
            }
          } else {
            if (
              this.dataMgr.isExistEvent0Obj(
                notiInfo,
                TYPE.EMAIL,
                ACTION.CREATE,
                { type: TYPE.DATAMANAGER, id: 'all' },
                this,
              ) === false
            ) {
              this.dataMgr.addEvent0Obj(
                notiInfo,
                TYPE.EMAIL,
                ACTION.MODIFY,
                { type: TYPE.DATAMANAGER, id: 'all' },
                this,
              )
            }
          }
        }
      } else {
        this.dataMgr.removeEvent0Obj(
          notiInfo,
          TYPE.EMAIL,
          ACTION.CREATE,
          { type: TYPE.DATAMANAGER, id: 'all' },
          this,
        )
        this.dataMgr.removeEvent0Obj(
          notiInfo,
          TYPE.EMAIL,
          ACTION.MODIFY,
          { type: TYPE.DATAMANAGER, id: 'all' },
          this,
        )
        this.dataMgr.addEvent0Obj(
          notiInfo,
          TYPE.EMAIL,
          ACTION.DESTROY,
          { type: TYPE.DATAMANAGER, id: 'all' },
          this,
        )
      }
    }
    this.isNew = false
    // 이미 로드된 메일에 검색 유래(fake+isOpenSearch) 데이터가 병합될 때는 재조회를 하지
    // 않으므로(위 OFM-225 분기) 로드 상태를 유지해야 한다 — false로 내리면 재조회 없이
    // skeleton으로 남는다. 그 외 fake는 기존대로 미로드 처리(재조회가 로드 상태를 복구).
    this.isLoaded = isFake !== true || preserveOpenSearch === true

    return isChanged
  }

  applyCalendarData = (calendarFile: any, subject: string | null = null) => {
    if (this.extData.calendarEvent == null) {
      if (subject != null) {
        const regexp = /^(Update|RSVP|Invitation|Accepted|Declined|Tentative|Canceled):(\s*)/i
        this.viewSubject = subject.replace(regexp, '')
      }

      this.extData.calendarEvent = {}

      download(calendarFile, false, false).then((ret: any) => {
        // console.log(' -- calendarFile : ', ret);
        const { from: fromList } = this.data
        const from = fromList != null ? fromList[0] : null
        const meetingInfo = getMeetingInfo(ret.text, from)
        if (meetingInfo == null) return
        this.extData.calendarEvent = {
          type: meetingInfo.type,
          meeting_info: meetingInfo,
        }
        this.reactMgr.updateUI()
      })
    }
  }

  calcFixedData = (data: any) => {
    if (data != null && data.messageId != null) {
      const { hasAttachment, attachments, subject } = data
      this.viewSubject = subject
      if (hasAttachment === true || attachments.length > 0) {
        let allAttachments = [...(attachments || [])]
        if (attachments.length > 0) {
          // name이 없고, type이 text/calendar인 첨부파일이 있으면 캘린더 이벤트로 처리.
          const calendarFile = attachments.find((attachment) => {
            const { type, name } = attachment
            return name == null && type != null && type.toLowerCase() === 'text/calendar'
          })
          if (calendarFile != null) {
            // const existICS = attachments.find((attachment) => {
            //   const { type, name, blobId } = attachment
            //   return (
            //     blobId === calendarFile.blobId &&
            //     ((type != null && type.toLowerCase() === 'application/ics') ||
            //       (name != null && name.toLowerCase().endsWith('.ics')))
            //   )
            // })
            this.applyCalendarData(calendarFile, subject)

            allAttachments = attachments.filter((attachment) => {
              return attachment.blobId !== calendarFile.blobId
            })
          }
        }

        // inline 이미지가 아닌 첨부파일 목록.
        this.nonInlineAttachments = allAttachments.filter((attachment) => {
          // return attachment.disposition === 'attachment'
          const { cid, contentId, disposition } = attachment
          const hasCid = cid != null || contentId != null
          return hasCid === false || disposition === 'attachment'
        })
      }
    }
  }

  getViewSubject = (): string => {
    return this.viewSubject || this.data.subject
  }

  getViewTime = (): string => {
    return this.data.receivedAt
  }

  // OMD-151: header:Received 유무로 보낸/받은 메일 판정.
  // - undefined: 미로딩/구 IndexedDB 캐시 → 불명(null)
  // - null: 헤더 부재(배달 안 됨) → 보낸 메일(true)
  // - 문자열: 헤더 존재(배달됨) → 받은 메일(false)
  isSentMail = (): boolean | null => {
    const received = this.data['header:Received']
    if (received === undefined) {
      return null
    }
    return received === null
  }

  // OMD-151: 자기발신 쌍 식별용 Message-ID. data.messageId는 string[]이므로 첫 요소(primary)를
  // 반환한다(두 사본은 동일 Message-ID 문자열을 가져 === 비교가 성립). 미로딩 시 null.
  getMessageId = (): string | null => {
    const messageId = this.data.messageId
    if (messageId == null) {
      return null
    }
    return messageId[0] ?? null
  }

  isFlagEnabled = (): boolean => {
    return true
  }

  hasAttachment = (): boolean => {
    if (this.nonInlineAttachments != null) {
      return this.nonInlineAttachments.length > 0
    }
    return this.data.hasAttachment === true
  }

  getPreview = (): string => {
    const previewStr = (str: string) => {
      if (str == null || str.trim().length === 0) {
        return i18n.t('email.desc.noPreview')
      }
      return str
    }

    if (useAiSummary() === true && this.data.messageId != null) {
      const aiSettings = settings.getValue('ai')
      const msgId = getMessageId(this.data.messageId[0])
      const aiData = aiSummaryMap.get(msgId)
      if (aiData != null) {
        const { concise_summary } = aiData
        const aiSummary = concise_summary[aiSettings.language]
        if (aiSummary != null && aiSummary.code === 200) {
          return previewStr(aiSummary.translation)
        }
      }
    }
    if (this.isLoaded === false) {
      // 로딩이 완료되지 않았으면, 미리보기를 표시하지 않는다.
      return null
    }
    // OFM-250: EUC-KR 이중 인코딩(mojibake) preview 를 표시 직전 보정한다.
    return previewStr(safePreview(this.data.preview))
  }

  getDeleteType = (): string => {
    const trash = this.dataMgr.getTrash()
    const drafts = this.dataMgr.getDrafts()

    let deleteType = 'delete'
    if (this.isContained(trash) === true) {
      deleteType = 'destroy'
    } else if (this.isContained(drafts) === true || this.isDraft() === true) {
      deleteType = 'destroy'
    }

    return deleteType
  }

  isContained = (mailbox: any): boolean => {
    return mailbox.isContain(this)
  }

  isDraft = (): boolean => {
    const checkKeyword = this.data.keywords != null && this.data.keywords.$draft === true
    if (checkKeyword === true) {
      return true
    }
    if (this.data != null && this.data.mailboxIds != null) {
      const drafts = this.dataMgr.getDrafts()
      const mailboxIds = this.data.mailboxIds
      return drafts != null && mailboxIds[drafts.getId()] === true
    }
    return false
  }

  applyRemove = (emailData: any, notiInfo: any, mailboxInfos: any) => {
    this.applyData(
      {
        ...emailData,
      },
      notiInfo,
      true,
      false,
      mailboxInfos,
    )
  }

  parseBodyPart = (part: any, rData?: any) => {
    const ret: any = rData || {}
    if (ret.files == null) {
      ret.files = []
    }
    const { partId, subParts, type: type0, size } = part
    const type = type0.toLowerCase()
    if (size >= 0 && subParts == null) {
      if (ret.htmlPartIds.includes(partId) && type === 'text/html') {
        ret.htmlParts.push(part)
      } else if (ret.textPartIds.includes(partId) && type === 'text/plain') {
        ret.textParts.push(part)
      } else {
        // 첨부파일 중에 content-type이 text/calendar이고, method가 있는 경우에는 캘린더 이벤트로 처리.
        // 기존에 저장된 데이터는 'header:Content-Type' 정보가 없으므로, 항상 캘린더 이벤트로 처리.
        if (type === 'text/calendar') {
          const contentType = part['header:Content-Type']
          if (contentType != null) {
            if (contentType.toLowerCase().indexOf('method=') >= 0) {
              ret.calendar = part
              this.applyCalendarData(part, this.data.subject)
            }
          }
          // else {
          //   emailDetailDB.deleteData(this.getId()).then(() => {
          //     this.detail = null;
          //     setTimeout(() => {
          //       this.reqDetail();
          //     }, 100);
          //   });
          // }
        }
        ret.files.push(part)
      }
    } else {
      if (type === 'multipart/alternative') {
        subParts.forEach((part: any) => {
          this.parseBodyPart(part, ret)
        })
      } else {
        subParts.forEach((subPart: any) => {
          this.parseBodyPart(subPart, ret)
        })
      }
    }
    return ret
  }

  setRead = (value: any, targetMailbox: any): any => {
    if (this.isLoaded !== true || this.data == null) {
      // @todo isLoaded가 true가 되었을 때 설정된 value가 반영되도록 처리해야 함.
      return Promise.resolve(true)
    }
    return this.dataMgr.setKeyword([this], '$seen', value === true, targetMailbox)
  }

  isRead = (): boolean => {
    if (this.isMailState(EMAIL_STATE.READING) === true) {
      return true
    } else if (this.isMailState(EMAIL_STATE.UNREADING) === true) {
      return false
    }
    return this.data.keywords != null && this.data.keywords.$seen === true
  }

  isFlagged = (): boolean => {
    if (this.isMailState(EMAIL_STATE.FLAGGING) === true) {
      return true
    } else if (this.isMailState(EMAIL_STATE.UNFLAGGING) === true) {
      return false
    }
    return this.data.keywords != null && this.data.keywords.$flagged === true
  }

  isOutcomeAdded = (): boolean => {
    return this.data.keywords != null && this.data.keywords.$outcome_added === true
  }

  isOutcomeUpdated = (): boolean => {
    return this.data.keywords != null && this.data.keywords.$outcome_updated === true
  }

  // reqDetail = async (reload?: boolean) => {
  //   const buffer = ``
  //   return new Promise(async (resolve) => {
  //     this.detail = {}
  //     const postalMimeEML = await PostalMime.parse(buffer)
  //     const bodyPart = await this.makeBodyPartFromEML(postalMimeEML)
  //     this.detail.bodyPart = bodyPart
  //     this.detail.hasAttachment = bodyPart.files != null && bodyPart.files.length > 0
  //     resolve(this.detail)
  //   })
  // }

  reqAllData = (mailboxObj: any) => {
    return new Promise((resolve, reject) => {
      const client = getJamClient()
      if (client == null) {
        reject(new Error('JMAP client not initialized'))
        return
      }
      client.api.Email.get({
        accountId: jam.accountId,
        ids: [this.getId()],
        properties: defaultEmailProp,
      })
        .then(([mailList]: any) => {
          if (mailList.list.length > 0) {
            const data = { ...mailList.list[0] }
            // console.info(' --- reqEmail test - process single response : ', this.getId())
            // 모든 데이터를 가져오므로 DB에 저장
            this.setEmailData(mailboxObj, data, true)
            // 정상 응답 = 서버 복구 신호(차단 해제 등).
            jmapReqQueue.notifyServerRecovery()
            resolve(data)
            return
          }
          // 응답에 없으면(삭제 등) 즉시 실패로 종료한다(resolve 후 reject 낙하 방지).
          reject(new Error('No email data'))
        })
        .catch((err: any) => {
          reject(err)
        })
    })
  }

  reqChangeableData = (mailboxObj: any) => {
    return new Promise((resolve, reject) => {
      const client = getJamClient()
      if (client == null) {
        reject(new Error('JMAP client not initialized'))
        return
      }
      client.api.Email.get({
        accountId: jam.accountId,
        ids: [this.getId()],
        properties: changeableEmailProp,
      })
        .then(([mailList]: any) => {
          if (mailList.list.length > 0) {
            const data = { ...mailList.list[0] }
            // 변경 가능한 데이터만 가져오므로 DB에 저장하지 않음
            this.setEmailData(mailboxObj, data, false)
            resolve(data)
            return
          }
          reject(new Error('No email data'))
        })
        .catch((err: any) => {
          reject(err)
        })
    })
  }

  reqDetail = (reload?: boolean): Promise<any> => {
    // 캐시 단락: detail이 이미 있고 강제 재요청이 아니면 즉시 반환
    if (reload !== true && this.detail != null) {
      return Promise.resolve(this.detail)
    }

    // in-flight 가드: 진행 중인 요청이 있으면 그 Promise를 공유한다.
    // reload=true로 강제 재요청 시에도 기존 flight가 있으면 그것을 기다린다
    // (동일 메일에 대해 중복 Email/get 방지).
    if (this._detailFlight != null) {
      return this._detailFlight
    }

    // 재시도 타이머가 남아 있으면 정리(새 요청이 들어왔으므로 이전 backoff 무효)
    if (this._detailRetryTimer != null) {
      clearTimeout(this._detailRetryTimer)
      detailRetryTimers.delete(this._detailRetryTimer)
      this._detailRetryTimer = null
    }

    this._detailFlight = new Promise((resolve, reject) => {
      const resolveDetail = (detail: any) => {
        resolve(detail)
      }

      const processDetail = async (detail: any, skipSave: boolean = false) => {
        if (skipSave !== true) {
          emailDetailDB.putData(this.getId(), detail)
        }
        this.detail = detail
        const { bodyValues, bodyStructure, blobId } = this.detail
        if (bodyValues != null && bodyStructure != null && bodyStructure.subParts != null) {
          const bodyPart = await this.makeBodyPartFromJMAP(this.detail)
          this.detail.bodyPart = bodyPart
          resolveDetail(this.detail)
        } else if (
          bodyStructure != null &&
          bodyStructure.size > 0 &&
          bodyStructure.blobId != null
        ) {
          const bodyPart = await this.makeBodyPartFromJMAP(this.detail)
          this.detail.bodyPart = bodyPart
          resolveDetail(this.detail)
        } else if (blobId != null) {
          const file = {
            blobId: blobId,
            name: '',
            type: '',
          }
          try {
            const ret = await download(file, false, true)
            const buffer = await ret.blob.arrayBuffer()
            const bodyPart = await this.parseEML(buffer)
            this.detail.bodyPart = bodyPart
            this.detail.hasAttachment = bodyPart.files != null && bodyPart.files.length > 0
            resolveDetail(this.detail)
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
          } catch (err: any | Error) {
            warningBase({
              content: 'Eml download or parsing error.',
            })
            resolveDetail(this.detail)
          }
        } else {
          resolveDetail({})
        }
      }

      const loadDetail = (attempt: number = 0) => {
        const client = getJamClient()
        if (client == null) {
          reject(new Error('JMAP client not initialized'))
          return
        }
        client.api.Email.get({
          accountId: jam.accountId,
          ids: [this.getId()],
          properties: detailEmailProp,
          bodyProperties: emailBodyProp,
          fetchTextBodyValues: true,
          fetchHTMLBodyValues: true,
        })
          .then(async ([mailList]: any) => {
            if (mailList.list.length > 0) {
              const detail = { ...mailList.list[0] }
              await processDetail(detail, false)
            } else if (mailList.notFound != null && mailList.notFound.indexOf(this.getId()) >= 0) {
              // 백엔드에 이메일이 이미 삭제된 상태. 두 단계로 처리:
              //
              //   1) 좀비 EmailObj 명시 정리 — dataMgr.allEmailMap / 각 mailbox.emailMap
              //      에서 해당 ID 의 정보 제거. reqUpdatedThreadData 가 sinceState 기반
              //      incremental sync 라 lastState 이전 destroy 는 못 잡을 수 있으므로
              //      여기서 확실히 정리.
              //   2) mailbox 별 reqUpdatedThreadData 호출 — 백엔드 변경 사항 동기화 +
              //      정식 알림 흐름 (mailbox 의 changeListener / dataMgr.notifyThreadChanged
              //      / notifyRelatedMailboxes 등) 위임. 직접 changeListener.notify 호출보다
              //      검증된 정식 sync 경로. 부수 변경 (다른 이메일) 도 함께 동기화.
              //
              // resolve 는 `{}` 로 — 호출자(예: ThreadView/emailHelper.ts:396 의 getBody)
              // 가 `const { bodyPart } = detail` 같은 destructuring 을 가정하므로 null 이면
              // TypeError. 빈 객체는 `if (bodyPart != null)` 가드로 안전 fallback.
              const notiInfo: any = {}

              // 현재 보고 있는 mailbox(curMailbox)가 thread-type(isEmailType === false)이면,
              // 삭제 이메일이 직접 속한 mailbox 가 아니어도 같은 thread 의 다른 이메일을 통해
              // 이 thread 를 표시 중일 수 있다. 그 경우 curMailbox 의 ThreadList 도 갱신 대상.
              // 기본 루프(this.data.mailboxIds)에서 이미 처리되는 경우는 제외(중복 방지).
              const curMailbox = this.dataMgr.curMailbox
              let isCurMailboxAffected = false
              if (
                curMailbox != null &&
                curMailbox.isEmailType === false &&
                this.data != null &&
                this.data.mailboxIds != null &&
                this.data.mailboxIds[curMailbox.getId()] !== true
              ) {
                const threadObj = this.dataMgr.getThreadById(this.data.threadId)
                if (threadObj != null) {
                  isCurMailboxAffected = threadObj.data.emailIds.some((emailId: string) => {
                    const emailObj = this.dataMgr.getEmailById(emailId)
                    return emailObj != null && curMailbox.isContain(emailObj) === true
                  })
                }
              }

              if (this.data != null && this.data.mailboxIds != null) {
                Object.keys(this.data.mailboxIds).forEach((mailboxId) => {
                  const mailbox = this.dataMgr.getMailboxById(mailboxId)
                  if (mailbox != null) {
                    mailbox.removeEmailData(this.data, notiInfo, true)
                  }
                })
              }
              if (isCurMailboxAffected === true) {
                curMailbox.removeEmailData(this.data, notiInfo, true)
              }

              this.dataMgr.removeEmail(this)

              // 백엔드에 이미 삭제된 이메일이므로 캐시(emailDB/emailDetailDB)도 제거.
              this.dataMgr.deleteEmailFromDB([this.getId()])

              if (this.data != null && this.data.mailboxIds != null) {
                Object.keys(this.data.mailboxIds).forEach((mailboxId) => {
                  const mailbox = this.dataMgr.getMailboxById(mailboxId)
                  if (mailbox != null) {
                    mailbox.reqUpdatedThreadData()
                  }
                })
              }
              if (isCurMailboxAffected === true) {
                curMailbox.reqUpdatedThreadData()
              }

              resolve({})
            } else {
              // empty-list(삭제된 메일 등)는 재시도 대상이 아님 — 즉시 실패 종료
              reject(new Error('No detail data'))
            }
          })
          .catch((err: any) => {
            // 네트워크/API 에러: online && 상한 미초과 시 backoff 후 재시도
            if (window.navigator.onLine === true && attempt < MAX_RETRY) {
              // 실패 표시: 이후 정상 응답 시 서버 복구 리스너(뷰어 본문 재요청 등)를 깨운다.
              jmapReqQueue.markFailure()
              const delay = Math.min(BASE_DELAY * 2 ** attempt, MAX_BACKOFF)
              const timer = setTimeout(() => {
                detailRetryTimers.delete(timer)
                this._detailRetryTimer = null
                // 재시도 전 캐시 무효화(부분 성공 대비)
                this.detail = null
                loadDetail(attempt + 1)
              }, delay)
              this._detailRetryTimer = timer
              detailRetryTimers.add(timer)
            } else {
              reject(err)
            }
          })
      }

      if (useEmailDetailDB === true) {
        emailDetailDB
          .getData(this.getId())
          .then((dbData) => {
            if (dbData != null) {
              const detail = {
                ...dbData.data,
              }
              processDetail(detail, true)
            } else {
              loadDetail()
            }
          })
          .catch((error) => {
            console.error('getEmailDataFromDB error', error)
            loadDetail()
          })
      } else {
        loadDetail()
      }
    }).finally(() => {
      this._detailFlight = null
    })

    return this._detailFlight
  }

  makeBodyPartFromJMAP = async (detail: any) => {
    const { bodyValues, bodyStructure, textBody, htmlBody, attachments /* , hasAttachment */ } =
      detail
    const info: any = {
      textBody,
      htmlBody,
      // textPartId: textBody != null && textBody.length > 0 ? textBody[0].partId : null,
      // htmlPartId: htmlBody != null && htmlBody.length > 0 ? htmlBody[0].partId : null,
      textPartIds:
        textBody != null && textBody.length > 0 ? textBody.map((part: any) => part.partId) : [],
      htmlPartIds:
        htmlBody != null && htmlBody.length > 0 ? htmlBody.map((part: any) => part.partId) : [],
      htmlParts: [],
      textParts: [],
    }
    const bodyPart: any = this.parseBodyPart(bodyStructure, info)
    info.calendarPartId = bodyPart.calendar != null ? bodyPart.calendar.partId : null
    const isCalendar = bodyPart.calendar != null
    let calendarBody = ''
    if (isCalendar === true) {
      if (bodyPart.calendar.partId != null && bodyValues[bodyPart.calendar.partId] != null) {
        calendarBody = bodyValues[bodyPart.calendar.partId].value
      } else if (bodyPart.calendar.blobId != null) {
        const file = {
          blobId: bodyPart.calendar.blobId,
          name: bodyPart.calendar.name,
          type: bodyPart.calendar.type,
        }
        const ret = await download(file, false, true)
        const buffer = await ret.blob.arrayBuffer()
        var decoder = new TextDecoder(bodyPart.calendar.charset || 'utf-8')
        calendarBody = decoder.decode(buffer)
      }
    }
    bodyPart.hasHTMLBody = () => {
      return bodyPart.htmlParts != null && bodyPart.htmlParts.length > 0
    }
    bodyPart.getHTMLBody = () => {
      // @todo Html 조합하는 방식을 나중에 개선할 것.
      let htmlBody = ''
      if (bodyPart.htmlParts != null && bodyPart.htmlParts.length > 0) {
        bodyPart.htmlParts.forEach((part: any) => {
          if (part.partId != null && bodyValues[part.partId] != null) {
            htmlBody += bodyValues[part.partId].value
          }
        })
      }
      return htmlBody
    }
    bodyPart.hasTextBody = () => {
      return bodyPart.textParts != null && bodyPart.textParts.length > 0
    }
    bodyPart.getTextBody = () => {
      let textBody = ''
      if (bodyPart.textParts != null && bodyPart.textParts.length > 0) {
        bodyPart.textParts.forEach((part: any) => {
          if (part.partId != null && bodyValues[part.partId] != null) {
            textBody += bodyValues[part.partId].value
          }
        })
      }
      return textBody
    }
    bodyPart.isCalendar = () => {
      return isCalendar
    }
    bodyPart.getCalendarBody = () => {
      return calendarBody
    }
    if (attachments != null && attachments.length > 0) {
      // 본문으로 실제 렌더된 파트(text/html·text/plain)와 캘린더 파트만 첨부에서 제외한다.
      // htmlBody 배열에는 HTML 파트 사이에 interleave된 이미지 파트(Apple Mail 등)가 포함될 수
      // 있는데, htmlBody의 partId 전체를 제외하면 이런 이미지가 본문에도 첨부에도 없이 유실된다.
      const renderedPartIds = [...bodyPart.htmlParts, ...bodyPart.textParts].map(
        (part: any) => part.partId,
      )
      bodyPart.files = attachments.filter((file: any) => {
        return (
          renderedPartIds.includes(file.partId) === false && file.partId !== info.calendarPartId
        )
      })
    }
    // attachments가 없고, subparts로만 inline image가 있는 상태일 때 처리.
    // @todo - type이 'multipart/mixed'에서만 발생하는 현상인지 확인할 것.
    // else {
    //   bodyPart.files = []
    // }
    return bodyPart
  }

  loadSelf = async (useNoti?: boolean) => {
    if (this.isLoaded === false || this.data.messageId == null) {
      if (this.selfLoadingList == null) {
        this.selfLoadingList = []

        let getEmailsResult: Awaited<ReturnType<typeof getEmailsByIds>> | null = null
        try {
          getEmailsResult = await getEmailsByIds([this.getId()], true)
        } catch (err) {
          this.selfLoadingList.forEach((item) => {
            item.reject(err)
          })
          this.selfLoadingList = null
          throw err
        }

        const { emailList, threadList } = getEmailsResult

        if (emailList != null && emailList.length > 0) {
          let mailbox = null
          emailList.some((emailData: any) => {
            if (this.getId() === emailData.id) {
              Object.keys(emailData.mailboxIds).some((mailboxId) => {
                mailbox = this.dataMgr.getMailboxById(mailboxId)
                return true
              })
              return true
            }
            return false
          })
          const notiInfo = useNoti === true ? {} : null
          emailList.forEach((emailData: any) => {
            if (mailbox.isEmailType === false || this.getId() === emailData.id) {
              Object.keys(emailData.mailboxIds).forEach((mailboxId) => {
                const pMailbox = this.dataMgr.getMailboxById(mailboxId)
                const fakeEmailData = {
                  id: emailData.id,
                  ...emailData,
                  extra: {
                    isFake: true,
                    mailboxObj: pMailbox,
                  },
                }
                pMailbox.addEmailData(fakeEmailData, notiInfo, true)
              })
            }
          })

          if (mailbox.isEmailType === false && threadList != null) {
            threadList.forEach((threadData) => {
              const threadObj = mailbox.addThreadData(threadData, notiInfo, false)
              const id = this.getId()
              const titleEmail = mailbox.getEmailById(id)
              if (titleEmail != null) {
                threadObj.titleEmail = titleEmail
                threadObj.compEmail = titleEmail
              }
            })
          }
          if (notiInfo != null) {
            const eventList = this.dataMgr.makeEvent0(notiInfo)
            const notiEvent = eventList.filter((item: any) => {
              return item.type === TYPE.THREAD || item.type === TYPE.EMAIL
            })
            this.dataMgr.notifyThreadChanged(notiEvent)
            const notiMap: any = {}
            notiEvent.forEach((event: any) => {
              if (event.targetType === TYPE.MAILBOX) {
                const mailboxId = event.target.getId()
                if (notiMap[mailboxId] == null) {
                  notiMap[mailboxId] = []
                }
                notiMap[mailboxId].push(event)
              }
            })
            Object.entries(notiMap).forEach(([mailboxId, notiList]: any) => {
              const mailbox = this.dataMgr.getMailboxById(mailboxId)
              mailbox.threadChangeListener.notify(notiList)
            })
          }

          this.processMailbox()
          // if (mailbox != null) {
          //   mailbox.sortEmail()
          //   mailbox.sortThread()
          // }

          this.selfLoadingList.forEach((item: any) => {
            item.resolve(this)
          })
          this.selfLoadingList = null
          return this
        }

        this.selfLoadingList.forEach((item: any) => {
          item.resolve(null)
        })
        this.selfLoadingList = null
        return null
      } else {
        const promise = new Promise((resolve, reject) => {
          this.selfLoadingList.push({ resolve, reject })
        })
        return promise
      }
    }

    this.processMailbox()
    return this
  }

  processMailbox = () => {
    if (this.data.mailboxIds != null) {
      let mailbox = null
      Object.keys(this.data.mailboxIds).some((mailboxId) => {
        mailbox = this.dataMgr.getMailboxById(mailboxId)
        return true
      })
      if (mailbox != null) {
        if (mailbox.isEmailType === false) {
          const threadId = this.data.threadId
          let pos = mailbox.threadList.findIndex((threadObj) => {
            return threadObj.getId() === threadId
          })
          const threadObj = this.dataMgr.getThreadById(threadId)
          if (pos < 0) {
            mailbox.threadList.push(threadObj)
          }

          const isEmailExist = mailbox.emailList.some((emailObj) => {
            return emailObj.data.threadId === threadId
          })
          if (isEmailExist === false) {
            threadObj.data.emailIds.forEach((emailId: any) => {
              const emailObj = this.dataMgr.getEmailById(emailId)
              if (emailObj != null && mailbox.emailList.indexOf(emailObj) < 0) {
                mailbox.emailList.push(emailObj)
              }
            })
          }
        } else {
          let pos = mailbox.emailListForEmail.findIndex((emailObj) => {
            return emailObj.getId() === this.getId()
          })
          if (pos < 0) {
            mailbox.emailListForEmail.push(this)
          }

          pos = mailbox.emailList.findIndex((emailObj) => {
            return emailObj.getId() === this.getId()
          })
          if (pos < 0) {
            mailbox.emailList.push(this)
          }
        }
      }
    }
  }

  generateQHeadStr = () => {
    const emailStr = (item: any) => {
      if (item.name != null && item.email !== item.name) {
        return `${item.name}&lt;${item.email}&gt;`
      }
      return item.email
    }
    return this.reqDetail()
      .then((detail: any) => {
        const { from, receivedAt, to, cc, subject } = detail
        const fromStr = emailStr(from[0])
        const toStr = (to || [])
          .map((item: any) => {
            return emailStr(item)
          })
          .join('; ')
        let ccTag = ''
        if (cc != null && cc.length > 0) {
          const ccStr = (cc || [])
            .map((item: any) => {
              return emailStr(item)
            })
            .join('; ')
          ccTag = `
        <b>${i18n.t('email.field.cc')}:</b> ${ccStr}<br>
        `
        }
        // const { dateTimeFormat } = settings.getTimeFormat()
        const receiveDate = moment(receivedAt).format('LLLL')
        const headerOptions = { fromStr, receiveDate, toStr, ccTag, subject }

        const head = this.getQuotedHeaderStr(headerOptions)
        return head
      })
      .catch((err: unknown) => {
        console.error('[JMAPMailbox] generateQHeadStr reqDetail failed', err)
        return ''
      })
  }

  downloadEml = async (signal?: AbortSignal) => {
    const { blobId } = this.detail
    if (blobId == null) {
      return Promise.resolve(null)
    }

    // signal 이 진입 시점에 이미 aborted 면 download 호출 자체를 skip.
    // (onBeforeSend 안에서 xhr.abort() 후 return 하면 restClient.processXHR 가 그대로
    //  xhr.send() 를 호출하여 InvalidStateError 가 발생하므로 진입 전에 차단한다.)
    if (signal?.aborted === true) {
      throw new DOMException('Export aborted', 'AbortError')
    }

    if (this.dnObj === undefined) {
      let fileName = this.detail.subject
      if (fileName == null || fileName.length === 0) {
        fileName = i18n.t('editor.value.noSubject')
      } else {
        fileName = fileName.replace(/[ ]/g, '_')
      }
      const file = {
        blobId,
        name: `${fileName}.eml`,
        type: 'message/rfc822',
      }
      // download 진행 중 abort 가 발화하면 xhr.abort() 가 호출되어 진행 중 요청이 끊긴다.
      // 이미 aborted 인 케이스는 위에서 throw 되어 여기 도달하지 않는다.
      const onBeforeSend =
        signal == null
          ? undefined
          : (xhr: XMLHttpRequest) => {
              signal.addEventListener('abort', () => xhr.abort(), { once: true })
            }
      try {
        this.dnObj = await download(file, true, true, undefined, onBeforeSend)
      } catch (e) {
        // abort 로 인한 reject 는 호출자(ExportController) 가 abortRequested 검사로
        // 'aborted' 분류할 수 있도록 rethrow. 일반 에러만 무시한다.
        if (signal != null && signal.aborted) {
          throw e
        }
        console.error('download error - ', e)
      }
    }
    return Promise.resolve(this.dnObj)
  }

  buildEML = async (attachmentList: any[]) => {
    let detail: any
    try {
      detail = await this.reqDetail()
    } catch (err) {
      console.error('[JMAPMailbox] buildEML reqDetail failed', err)
      return { eml: null, error: err }
    }
    const { from, to, cc, headers, subject, bodyPart } = detail
    const covertEmailList = (list: any) => {
      if (list == null) {
        return null
      }
      return list.map((item: any) => {
        const { name, email } = item
        return {
          name: base64utf8String(name),
          email,
        }
      })
    }
    const data: any = {
      from: covertEmailList(from),
      to: covertEmailList(to),
      cc: covertEmailList(cc),
      headers,
      subject: base64utf8String(subject),
      // multipart: 'mixed',
      // multipartAlternative: {
      //   'Content-Type': 'multipart/alternative',
      // },
    }
    if (headers != null && headers.length > 0) {
      const headerMap: any = {}
      headers.forEach((header: any) => {
        headerMap[header.name] = header.value
      })
      data.headers = headerMap
    }
    let contentTypeCnt = 0
    if (bodyPart.hasTextBody() === true) {
      contentTypeCnt += 1
      data.text = bodyPart.getTextBody()
    }
    if (bodyPart.hasHTMLBody() === true) {
      contentTypeCnt += 1
      data.html = bodyPart.getHTMLBody()
    } else if (bodyPart.isCalendar() === true) {
      contentTypeCnt += 1
      data.html = bodyPart.getCalendarBody()
    }
    if (contentTypeCnt < 2) {
      // content에 해당하는 text/plain 또는 text/html 중 하나만 있는 경우 사용가능.
      // alternative로 처리하는 경우, text/plain이 없으면 JMAP서버에서 preview 생성이 안됨.
      data.multipart = 'mixed'
    } else {
      // content에 해당하는 text/plain 과 text/html이 모두 있는 경우에는 alternative로 처리.
      // mixed로 처리하는 경우, postalmime에서 파싱할 때 문제가 발생함.(본문 내용이 중복 표시됨.)
      data.multipartAlternative = {
        'Content-Type': 'multipart/alternative',
      }
    }
    if (attachmentList != null && attachmentList.length > 0) {
      const promiseList = attachmentList.map(async (attachment: any) => {
        const { name, type, size, isInline, cid } = attachment.file
        const ret: any = {
          filename: base64utf8String(name),
          size,
        }
        if (type != null) {
          ret.contentType = type
        }
        if (isInline === true) {
          ret.inline = true
        }
        if (cid != null) {
          ret.cid = cid
        }
        const downloadObj = await attachment.download()
        ret.data = downloadObj.blob
        return ret
      })
      const retList = await Promise.all(promiseList)
      data.attachments = retList
    }
    const { eml, error } = await buildEml(data)
    if (error != null) {
      console.error('build error - ', error)
    }
    return { eml, error }
  }

  saveEML = async (attachmentList: any[]) => {
    const { blobId } = this.detail
    if (blobId == null) {
      const { eml, error } = await this.buildEML(attachmentList)
      if (eml != null) {
        const blob = new Blob([eml], { type: 'message/rfc822' })
        let fileName = this.detail.subject
        if (fileName == null || fileName.length === 0) {
          fileName = i18n.t('editor.value.noSubject')
        } else {
          fileName = fileName.replace(/[ ]/g, '_')
        }
        // 1회성 다운로드 자원은 click() 시점에 다운로드가 시작되므로 즉시 revoke한다
        // (exportMessage의 zip 다운로드와 동일 패턴, trackObjectURL 적용 대상 아님).
        const blobUrl = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = generateFilename(`${fileName}.eml`)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(blobUrl)
      }
      return { eml, error }
    }
    const ret: any = {}
    try {
      const dnObj = await this.downloadEml()
      if (dnObj != null && dnObj.saveFile != null) {
        dnObj.saveFile()
        ret.eml = dnObj
      } else {
        ret.error = new Error('save file failed')
      }
    } catch (e) {
      ret.error = e
    }
    return ret
  }

  getPrimaryMessageId = (): string | null => {
    const headers: Array<{ name: string; value: string }> = this.data?.headers ?? []
    const references = headers.find((h) => h.name.toLowerCase() === 'references')
    if (references != null) {
      const parts = references.value.trim().split(' ')
      return parts[0] ?? null
    }
    const messageId = headers.find((h) => h.name.toLowerCase() === 'message-id')
    return messageId?.value ?? null
  }

  getEmlBlob = async (signal?: AbortSignal) => {
    const { blobId } = this.detail
    if (blobId == null) {
      const { attachments } = this.detail
      const attachmentList = attachments.map((file) => {
        return attachmentHelper.createAttachmentFromDraft(file, this as any)
      })
      const { eml } = await this.buildEML(attachmentList)
      if (eml != null) {
        let fileName = this.detail.subject
        if (fileName == null || fileName.length === 0) {
          fileName = i18n.t('editor.value.noSubject')
        } else {
          fileName = fileName.replace(/[ ]/g, '_')
        }
        const blob = new File([eml], `${fileName}.eml`, { type: 'message/rfc822' })
        return blob
      }
      return null
    }

    try {
      const dnObj = await this.downloadEml(signal)
      if (dnObj != null && dnObj.blob != null) {
        const { name, type } = dnObj.file
        const blob = new File([dnObj.blob], name, { type })
        return blob
      }
    } catch (e) {
      console.error('get eml blob error - ', e)
    }
    return null
  }
}

// JMAPMailbox가 MailboxObj도 extends 하도록 변경
export class JMAPMailbox extends MailboxObj implements JMAPMailboxInterface {
  position: number
  limit: number
  maxChanges: number
  isFirstLoading: boolean
  hasMoreData: boolean
  // OMD-107: 페이지네이션 리셋 세대. resetReqData()마다 증가하여, 리셋 이전에 발사된
  // in-flight 요청의 stale 응답을 논리적으로 무효화한다(응답 핸들러에서 세대 불일치 시 폐기).
  reqGeneration = 0
  enableQueryChange: any
  searchText: string | null
  delayedDataMap: any
  list: any
  prevResultUI: any
  _listType: any
  clearDelayedData: any
  useUnreadCnt: any
  notifyCountChanged: any
  prevCount: any
  addDelayedData: any
  options: any
  sortFunc: any
  innerMailbox: any
  searchMode: boolean | undefined
  // ... 필요한 멤버 변수 추가 ...
  lastState: number | undefined
  lastQueryState: number | undefined
  limitEmailId: string | undefined
  // OMD-21: 검색(OpenSearch) 결과의 매치된 emailId 누적 집합. THREAD 표시 모드에서 스레드
  // 형제 메일이 emailList 에 섞여도, 다음 페이지 position 을 "매치된 emailId 수"(Option A)로
  // 계산해 OpenSearch offset 이 어긋나지 않게 한다. 검색 세션 시작 시 초기화.
  searchMatchedIds: Set<string> = new Set<string>()

  // sortEmailTimer: any
  // sortThreadTimer: any

  constructor(dataMgr: any) {
    super(dataMgr)
    this.position = 0
    this.limit = 20
    this.maxChanges = 20
    this.data = {}
    this.isFirstLoading = true
    this.hasMoreData = true
    this.threadList = []
    this.emailList = []
    this.isEmailType = false
    this.isRealMailbox = true
    this.enableQueryChange = null
    this.emailListForEmail = []
    this.searchText = null
    this.delayedDataMap = {}
    this.list = undefined
    this.prevResultUI = undefined
    this._listType = undefined
    this.clearDelayedData = () => {}
    this.useUnreadCnt = undefined
    this.notifyCountChanged = () => {}
    this.prevCount = undefined
    this.addDelayedData = () => {}
    this.options = undefined
    this.sortInfo = undefined
    this.sortFunc = sortByReceivedAt
    this.innerMailbox = undefined

    this.lastState = undefined
    this.lastQueryState = undefined

    this.limitEmailId = undefined

    // this.sortEmailTimer = null
    // this.sortThreadTimer = null

    // this.threadChangeListener = new ListenerObj('ThreadListChange', this) // 제거
  }

  setLimit = (limit: number) => {
    this.limit = limit
  }

  getLastState = () => {
    return this.lastState
  }

  setLastState = (state: number) => {
    this.lastState = state
  }

  getLastQueryState = () => {
    return this.lastQueryState
  }

  setLastQueryState = (state: number) => {
    this.lastQueryState = state
  }

  getReqState = () => {
    return this.position
  }

  resetData = () => {
    this.threadList = []
    this.emailList = []
    this.emailListForEmail = []
    this.sortInfo = undefined
    this.sortFunc = sortByReceivedAt
    this.limitEmailId = undefined

    this.resetReqData()
  }

  resetReqData = () => {
    // OMD-107: 세대를 증가시켜 리셋 이전에 발사된 in-flight 요청의 응답을 무효화한다.
    this.reqGeneration += 1

    this.setLastState(undefined)
    this.setLastQueryState(undefined)

    this.isFirstLoading = true
    this.hasMoreData = true
    this.position = 0
  }

  // 다음 페이지 position을 절대 offset(position += limit) 대신 "보유 항목 중 여전히
  // 서버 리스트에 남는(필터 통과) 수"로 재계산할지 여부. 서버 목록이 도중에 당겨질 수
  // 있는 mailbox(가상 폴더 OFM-210, 검색 모드 OFM-225)는 절대 offset이 어긋나
  // 경계 항목이 누락되므로 로컬 개수 기준을 쓴다.
  useLocalPositionCalc = (): boolean => {
    return this.isRealMailbox !== true
  }

  applyData: (data: any) => any = (data: any) => {
    const targetData = this.data
    const isChanged = applyData(data, targetData)
    this.isEmailType = this.getListType() === TYPE.EMAIL
    return isChanged
  }

  addEmailForEmailType = (emailId: any) => {
    if (this.isEmailType === true) {
      const email = this.getEmailById(emailId)
      const existIdx = this.emailListForEmail.findIndex((emailItem: any) => {
        return emailItem.getId() === emailId
      })
      if (email != null && existIdx < 0) {
        this.emailListForEmail.push(email)
      }
    }
  }

  removeEmailForEmailType = (emailId: any) => {
    if (this.isEmailType === true) {
      const existIdx = this.emailListForEmail.findIndex((email: any) => {
        return email.getId() === emailId
      })
      if (existIdx > -1) {
        this.emailListForEmail.splice(existIdx, 1)
      }
    }
  }

  getMailboxFilter = () => {
    const mailboxIds = []
    const archiveMailbox = dataManager.getMailboxForRole('archive')
    if (archiveMailbox != null) {
      mailboxIds.push(archiveMailbox.getId())
    }
    const junkMailbox = dataManager.getMailboxForRole('junk')
    if (junkMailbox != null) {
      mailboxIds.push(junkMailbox.getId())
    }
    const sentMailbox = dataManager.getMailboxForRole('sent')
    if (sentMailbox != null) {
      mailboxIds.push(sentMailbox.getId())
    }
    const trashMailbox = dataManager.getMailboxForRole('trash')
    if (trashMailbox != null) {
      mailboxIds.push(trashMailbox.getId())
    }
    // outbox가 있는 경우에는 outbox도 제외.
    // const draftMailbox = dataManager.getMailboxForRole('drafts')
    // if (draftMailbox != null) {
    //   mailboxIds.push(draftMailbox.getId())
    // }
    const filterFunc = (emailData) => {
      const isInvalidMailbox = mailboxIds.some((id) => emailData.mailboxIds[id] === true)
      return isInvalidMailbox !== true
    }
    return filterFunc
  }

  getAllData = (): MailboxData => {
    if (this.isRealMailbox === false) {
      const resultFilter = this.getResultFilter()
      const mailboxFilter = this.getMailboxFilter()

      this.emailList = this.emailList.filter((emailObj: any) => {
        const delayed = this.delayedDataMap[emailObj.getId()]
        if (delayed != null) {
          // mailboxIds 소실(파괴 등) 시 보존하지 않는다.
          return delayed.data.mailboxIds != null && mailboxFilter(delayed.data)
        }
        return resultFilter(emailObj.data)
      })

      if (this.isEmailType === true) {
        this.emailListForEmail = this.emailListForEmail.filter((emailObj: any) => {
          const delayed = this.delayedDataMap[emailObj.getId()]
          if (delayed != null) {
            // mailboxIds 소실(파괴 등) 시 보존하지 않는다.
            return delayed.data.mailboxIds != null && mailboxFilter(delayed.data)
          }
          return resultFilter(emailObj.data)
        })
      }
    }

    return super.getAllData()
  }

  setEmailSearch = (text: string | null) => {
    this.resetData()

    this.searchText = text
  }

  // self.getEmailById = (id) => {
  //   return self.dataMgr.getEmailById(id)
  // }

  // self.getThreadById = (id) => {
  //   return self.dataMgr.getThreadById(id)
  // }

  getEmailById = (id: any): any => {
    // const email =self.emailList.find((item) => {
    //   return item.data.id === id
    // })

    const email = this.dataMgr.getEmailById(id)
    return email
  }

  getThreadById = (id: any) => {
    // 자신에게 포함된 thread 중에서 찾는다.
    let thread = this.threadList.find((item: any) => {
      return item.getId() === id
    })

    if (thread == null) {
      thread = this.dataMgr.getThreadById(id)
      const isContain = thread.data.emailIds.some((emailId: any) => {
        const email = this.getEmailById(emailId)
        if (email != null) {
          return email.data.mailboxIds[this.getId()] === true
        }
        return false
      })
      if (isContain !== true) {
        thread = null
      }
    }

    return thread
  }

  getEmailIdx = (email: any) => {
    if (email == null) {
      return -1
    }
    const { id } = email.data != null ? email.data : email
    return this.emailList.findIndex((item: any) => {
      return item.getId() === id
    })
  }

  getThreadIdx = (thread: any) => {
    const { id } = thread.data != null ? thread.data : thread
    return this.threadList.findIndex((item: any) => {
      return item.getId() === id
    })
  }

  addEmailData = (emailData: any, notiInfo: any, runSort: any) => {
    const mailboxInfos =
      this.isRealMailbox === true
        ? null
        : [
            {
              mailbox: this,
              action: this.getEmailIdx(emailData) < 0 ? ACTION.ADD : ACTION.UPDATE,
            },
          ]
    const email = this.dataMgr.applyEmailData(emailData, notiInfo, runSort, mailboxInfos)
    if (email != null) {
      const thread = this.dataMgr.getThreadById(email.data.threadId)
      thread.addEmail(email, notiInfo, true, mailboxInfos)
      this.addEmailForEmailType(emailData.id)
      if (this.isRealMailbox !== true) {
        this.addEmail_no_noti(email, runSort)
      } else if (runSort === true) {
        this.sortEmail()
      }
    }
    return email
  }

  removeEmailData = (emailData: any, notiInfo: any, removeFromThread: any = false) => {
    const mailboxInfos =
      this.isRealMailbox === true
        ? null
        : [
            {
              mailbox: this,
              action: ACTION.DELETE,
            },
          ]
    const email = this.getEmailById(emailData.id)
    if (email != null) {
      if (this.isContain(email) === true) {
        if (this.isRealMailbox !== true) {
          this.removeEmail_no_noti(email)
        }
        this.removeEmailForEmailType(emailData.id)
        email.applyRemove(emailData, notiInfo, mailboxInfos)
      }

      if (removeFromThread === true) {
        // move인 경우에는 thread에서 제거되지 않는다.
        const thread = this.getThreadById(email.data.threadId)
        if (thread != null) {
          thread.removeEmail(email, notiInfo, mailboxInfos)
        }
      }
    }
    return email
  }

  getThreadEmailfilter = () => {
    const visibleThreadItems = settings.getValue('email').visibleThreadItems
    let removeThreadItems = Object.values(MAILBOX_TYPE.typeDef).map((valueObj: any) => {
      return (valueObj as any).value
    })
    if (visibleThreadItems != null && visibleThreadItems.length > 0) {
      removeThreadItems = removeThreadItems.filter((value: any) => {
        return visibleThreadItems.indexOf(value) === -1
      })
    }

    let exceptMailboxList = []

    if (removeThreadItems.length > 0) {
      exceptMailboxList = removeThreadItems
        .map((value) => {
          let mailbox = null
          switch (value) {
            case 0:
              mailbox = this.dataMgr.getMailboxForRole('sent')
              break
            case 1:
              mailbox = this.dataMgr.getMailboxForRole('trash')
              break
            case 2:
              mailbox = this.dataMgr.getMailboxForRole('archive')
              break
            case 3:
              mailbox = this.dataMgr.getMailboxForRole('junk')
              break
            default:
          }
          return mailbox
        })
        .filter((mailbox) => {
          return mailbox != null && mailbox !== this
        })
    }

    const threadEmailfilter = (emailObj: any) => {
      if (emailObj == null || emailObj.isMailState(EMAIL_STATE.NORMAL) !== true) {
        return false
      }

      if (exceptMailboxList.length > 0) {
        const isExcepted = exceptMailboxList.some((mailbox) => {
          return mailbox.isContain(emailObj) === true
        })

        return isExcepted !== true
      }

      // const trash = this.dataMgr.getTrash()
      // if (trash != null) {
      //   const isTrash = this === trash
      //   if (isTrash !== true) {
      //     return trash.isContain(emailObj) === false
      //   }
      // }
      return true
    }

    return threadEmailfilter
  }

  addThreadData = (threadData: any, notiInfo: any, runSort: any) => {
    const mailboxInfos =
      this.isRealMailbox === true
        ? null
        : [
            {
              mailbox: this,
              action: this.getThreadIdx(threadData) < 0 ? ACTION.ADD : ACTION.UPDATE,
            },
          ]
    const thread = this.dataMgr.applyThreadData(threadData, notiInfo, runSort, mailboxInfos)
    return thread
  }

  addEmail_no_noti = (email: any, runSort: any) => {
    const pos = this.getEmailIdx(email)
    if (pos < 0) {
      this.emailList.push(email)
      // mailbox 자체에서 추가되지 않은 것은 EmailType에서 사용되지 않는다.
      // self.addEmailForEmailType(email.data.id);

      if (runSort !== false) {
        this.sortEmail()
      }
      return email
    }
    return null
  }

  removeEmail_no_noti = (email: any) => {
    const pos = this.getEmailIdx(email)
    if (pos > -1) {
      this.emailList.splice(pos, 1)
      // mailbox 자체에서 추가되지 않은 것은 EmailType에서 사용되지 않는다.
      // self.removeEmailForEmailType(email.data.id);
      return email
    }
    return null
  }

  addThread_no_noti = (thread: any, runSort: any) => {
    const pos = this.getThreadIdx(thread)
    if (pos < 0) {
      this.threadList.push(thread)
      if (runSort !== false) {
        this.sortThread()
      }
      return thread
    }
    return null
  }

  removeThread_no_noti = (thread: any) => {
    const pos = this.getThreadIdx(thread)
    if (pos > -1) {
      this.threadList.splice(pos, 1)
      return thread
    }
    return null
  }

  isValidThread = (thread: any) => {
    const { emailIds } = thread.data
    const isValidThread = emailIds.some((emailId: any) => {
      const emailItem = this.dataMgr.getEmailById(emailId)
      return emailItem.data.mailboxIds[this.getId()] === true
    })
    return isValidThread
  }

  getViewCountInfo = (): { count: number | null; type: string } | null => {
    const role = this.getRole()
    if (role === 'drafts') {
      return {
        count: this.data.totalEmails,
        type: 'total',
      }
    }
    return {
      count: this.data.unreadEmails,
      type: 'unread',
    }
  }

  setRole = (role: any) => {
    let isEnabled = false
    switch (role) {
      case '':
      case 'inbox':
      case 'archive':
      case 'drafts':
      case 'sent':
      case 'junk':
      case 'trash':
        // case 'important':
        isEnabled = true
        break
      default:
    }

    if (isEnabled === true) {
      const update = {}
      let prevMailbox = null
      if (role != null && role.length > 0) {
        prevMailbox = this.dataMgr.getMailboxForRole(role)
        if (prevMailbox != null) {
          update[prevMailbox.getId()] = { role: '' }
        }
      }
      update[this.getId()] = { role }

      return updateMailboxes(update).then((ret) => {
        if (prevMailbox != null) {
          const updated = ret[prevMailbox.getId()]
          if (updated != null) {
            prevMailbox.data.role = updated.role

            this.dataMgr.mailbox.changeListener.notify({
              action: ACTION.UPDATE,
              type: TYPE.MAILBOX,
              data: {
                item: prevMailbox,
              },
            })
          }
        }

        const updated = ret[this.getId()]
        if (updated != null) {
          this.data.role = updated.role

          this.dataMgr.mailbox.changeListener.notify({
            action: ACTION.UPDATE,
            type: TYPE.MAILBOX,
            data: {
              item: this,
            },
          })

          this.isEmailType = this.getListType() === TYPE.EMAIL
        }
        return ret
      })
    } else {
      warningBase({
        title: 'Error',
        content: `Invalid role : ${role}`,
      })
    }
  }

  rename = (newName: any) => {
    const update = {
      [this.getId()]: {
        name: newName,
      },
    }
    return updateMailboxes(update).then((ret) => {
      if (ret.error == null) {
        const updated = ret[this.getId()]
        if (updated.name != null) {
          // 즐겨찾기 항목은 경로 기반이므로 이름 반영 전후 경로로 다시 쓴다.
          const prevFavoriteEntry = this.dataMgr.getFavoriteEntryForMailbox(this)
          this.data.name = updated.name
          this.dataMgr.onFavoriteMailboxPathChanged(
            prevFavoriteEntry,
            this.dataMgr.getFavoriteEntryForMailbox(this),
          )

          this.dataMgr.sortMailbox()

          this.dataMgr.mailbox.changeListener.notify({
            action: ACTION.UPDATE,
            type: TYPE.MAILBOX,
            data: {
              item: this,
            },
          })
        }
      }
      return ret
    })
  }

  getParentMailbox = (): any => {
    return this.data.parentId != null ? this.dataMgr.getMailboxById(this.data.parentId) : null
  }

  isContain = (email: any): boolean => {
    if (email == null || email.data == null || email.data.mailboxIds == null) {
      return false
    }
    const id = this.getId()
    return id === '' || email.data.mailboxIds[id] === true
  }

  getEmailDeleteType = (): string => {
    const role = this.getRole()
    if (role === 'trash' || role === 'drafts') {
      return 'destroy'
    }
    return 'delete'
  }

  getToolbarMode = (): string => {
    const role = this.getRole()
    if (role === 'drafts') {
      return 'onlyDelete'
    }
    return 'all'
  }

  changeParent = (newParentId: any, newName?: any) => {
    const targetId = newParentId === '__root__' ? null : newParentId
    const updateData: any = {
      parentId: targetId,
    }
    if (newName != null) {
      ;(updateData as any).name = newName
    }

    const update = {
      [this.getId()]: updateData,
    }

    return updateMailboxes(update).then((ret) => {
      if (ret.error == null) {
        const updated = ret[this.getId()]
        if (updated.parentId !== undefined) {
          // 즐겨찾기 항목은 경로 기반이므로 이동 전후 경로로 다시 쓴다(하위 폴더 포함).
          const prevFavoriteEntry = this.dataMgr.getFavoriteEntryForMailbox(this)
          const prevParent = this.getParentMailbox()
          const prevList = prevParent != null ? prevParent.list : this.dataMgr.mailbox.list
          const pos = prevList.indexOf(this)
          if (pos > -1) {
            prevList.splice(pos, 1)
          }

          this.data.parentId = updated.parentId
          if (newName != null) {
            this.data.name = newName
          }
          this.dataMgr.onFavoriteMailboxPathChanged(
            prevFavoriteEntry,
            this.dataMgr.getFavoriteEntryForMailbox(this),
          )

          const curParent = this.getParentMailbox()
          if (curParent != null && curParent.list == null) {
            curParent.list = []
          }

          const curList = curParent != null ? curParent.list : this.dataMgr.mailbox.list
          curList.push(this)

          this.dataMgr.sortMailbox(curList)

          this.dataMgr.mailbox.changeListener.notify({
            action: ACTION.UPDATE,
            type: TYPE.MAILBOX,
            data: {
              item: this,
            },
          })
        }
      }
      return ret
    })
  }

  delete = (deleteEmails = false) => {
    return deleteMailbox(this.getId(), deleteEmails).then((ret) => {
      if (ret.deleted != null && ret.deleted.indexOf(this.getId()) > -1) {
        this.dataMgr.notifyMailboxDeleted(this)
      }
      return ret
    })
  }

  deleteChild = async (deleteSelf = true) => {
    let deleteArr = []

    const checkList = (mailbox: any, idx: number) => {
      if (mailbox.list != null && mailbox.list.length > 0) {
        mailbox.list.forEach((child: any) => {
          checkList(child, idx + 1)
        })
      }
      deleteArr.push({
        mailbox,
        idx,
      })
    }

    if (deleteSelf === true) {
      checkList(this, 0)
    } else if (this.list != null && this.list.length > 0) {
      this.list.forEach((child: any) => {
        checkList(child, 0)
      })
    }

    deleteArr = deleteArr.sort((a, b) => {
      return b.idx - a.idx
    })

    console.log(' -- deleteArr : ', deleteArr)

    for (let i = 0; i < deleteArr.length; i += 1) {
      const ret = await deleteArr[i].mailbox.delete(true)
      if (ret.error != null) {
        return ret
      }
    }

    return true
  }

  deleteEmails = (emailList: any) => {
    const removeEmailList = emailList.filter((email: any) => {
      return email.isMailState(EMAIL_STATE.NORMAL)
    })

    if (removeEmailList.length === 0) {
      return Promise.resolve({})
    }

    removeEmailList.forEach((email) => {
      email.setMailState(EMAIL_STATE.DELETING, true)
    })

    const clearMailState = () => {
      removeEmailList.forEach((email) => {
        email.setMailState(EMAIL_STATE.DELETING, false)
      })
    }

    return deleteEmails(emailList)
      .then(({ deletedEmail }) => {
        console.log(' -- deletedEmail : ', deletedEmail)

        const notiInfo = {}

        const mailboxIdMap = {}

        if (deletedEmail != null && deletedEmail.destroyed != null) {
          deletedEmail.destroyed.forEach((emailId) => {
            const email = this.dataMgr.getEmailById(emailId)
            if (email != null) {
              // 이메일이 포함된 thread가 관계된 모든 mailbox에 update 이벤트 전달.
              this.checkRelatedMailboxes(email, notiInfo)
              Object.keys(email.data.mailboxIds).map((mailboxId) => {
                mailboxIdMap[mailboxId] = true
              })
            }
          })
        }

        // 검색 모드에서 완전삭제된 메일은 검색 결과에 존재하지 않는 것으로 간주 —
        // movedEmailIds 등록 + 검색 목록에서 즉시 제거 + DELETE 이벤트 발행.
        // (이메일 객체가 전역에서 제거되기 전에 처리해야 하므로 applyEmailDeletedFromServer 앞)
        if (deletedEmail != null && deletedEmail.destroyed != null) {
          const searchMailbox = this.dataMgr.searchMailbox
          if (searchMailbox != null && searchMailbox.isSearchMode() === true) {
            searchMailbox.applyMovedEmails(deletedEmail.destroyed, notiInfo)
          }
        }

        this.applyEmailDeletedFromServer(deletedEmail.destroyed, notiInfo)

        // destroy 된 이메일은 캐시(emailDB/emailDetailDB)에서도 제거.
        if (deletedEmail.destroyed != null) {
          this.dataMgr.deleteEmailFromDB(deletedEmail.destroyed)
        }

        this.emailList = [...this.emailList]
        this.threadList = [...this.threadList]

        this.emailListForEmail = [...this.emailListForEmail]

        clearMailState()

        const eventList = this.dataMgr.makeEvent0(notiInfo)

        const notiEvent = eventList.filter((item) => {
          return item.type === TYPE.THREAD || item.type === TYPE.EMAIL
        })

        this.dataMgr.notifyThreadChanged(notiEvent)
        this.dataMgr.notifyRelatedMailboxes(notiEvent)

        emailList.forEach((email) => {
          Object.keys(email.data.mailboxIds).map((mailboxId) => {
            mailboxIdMap[mailboxId] = true
          })
        })

        const mailboxList = []
        Object.keys(mailboxIdMap).map((mailboxId) => {
          const mailbox = this.dataMgr.getMailboxById(mailboxId)
          if (mailbox != null && mailboxList.indexOf(mailbox) < 0) {
            mailboxList.push(mailbox)
          }
        })

        if (mailboxList.length > 0) {
          this.dataMgr
            .reqMailBox(mailboxList)
            .catch((err: unknown) => handleJmapCallError('deleteEmails', err))
        }

        return deletedEmail
      })
      .catch((err) => {
        console.error('deleteEmails error : ', err)
        clearMailState()
      })
  }

  deleteAllEmails = async () => {
    // await this.deleteEmails([...this.emailList])
    try {
      let emailIds = []

      const limit = 50
      let position = 0
      let res = null
      do {
        res = await requestEmailIds(this, { limit, position })
        if (res.emailIds.length > 0) {
          emailIds = [...emailIds, ...res.emailIds]
        }
        position += limit
      } while (res.emailIds.length === limit)

      console.log(' -- deleteAllEmails emailIds : ', emailIds)

      while (emailIds.length > 0) {
        const deleteIds = emailIds.length > limit ? emailIds.slice(-limit) : emailIds

        res = await deleteEmailsById(deleteIds)
        const { destroyed } = res.deletedEmail
        console.log(' -- deleteEmailsById : ', destroyed)

        if (destroyed != null && destroyed.length > 0) {
          // destroy 된 이메일은 캐시(emailDB/emailDetailDB)에서도 제거.
          this.dataMgr.deleteEmailFromDB(destroyed)
          emailIds = emailIds.filter((emailId) => {
            return destroyed.indexOf(emailId) < 0
          })
        }
      }

      this.dataMgr.reqMailBox().catch((err: unknown) => handleJmapCallError('deleteAllEmails', err))
      this.resetData()
      this.reqNextThreadList()

      return emailIds
    } catch (err) {
      console.log('deleteAllEmails error : ', err)
      throw err
    }
  }

  childEnabled = (): boolean => {
    const role = this.getRole()
    if (role === 'trash') {
      return false
    }
    return true
  }

  // setKeywordByEmailIds를 인스턴스 메서드로 선언
  setKeywordByEmailIds = async (emailIds: string[], subkey: string, val: boolean) => {
    const data = []

    const subkeyStr = `keywords/${subkey}`

    const emailState = this.dataMgr.getEMAILSTATEKEY(subkey, val)

    emailIds.forEach((id: any) => {
      data.push({
        id: id,
        [subkeyStr]: val === true ? true : null,
      })
      if (emailState > 0) {
        const email = this.dataMgr.getEmailById(id)
        if (email != null) {
          email.setMailState(EMAIL_STATE.KEYWORD_MASK, false)
          email.setMailState(emailState, true)
        }
      }
    })

    const clearEmailState = () => {
      if (emailState > 0) {
        emailIds.forEach((id: any) => {
          const email = this.dataMgr.getEmailById(id)
          if (email != null) {
            email.setMailState(emailState, false)
          }
        })
      }
    }

    const ret = await jamUpdate('Email', data)
    console.log(' -- setKeywordByEmailIds : ', ret)

    const [res] = ret
    const { updated } = res

    if (updated != null) {
      Object.keys(updated).forEach((id) => {
        const email = this.dataMgr.getEmailById(id)
        if (email != null) {
          const data = {
            id,
            keywords: {
              ...email.data.keywords,
              $seen: val,
            },
          }

          this.addEmailData(data, null, false)
        }
      })
    }

    clearEmailState()

    return ret
  }

  markAllasRead = async () => {
    // await this.deleteEmails([...this.emailList])

    // const resultObj = showResult({
    //   loading: true,
    //   closeOnClick: false,
    //   message: null,
    //   duration: 5, // second.
    // })

    try {
      let emailIds = []

      const limit = 50
      let position = 0
      let res = null
      do {
        res = await requestEmailIds(this, { limit, position, filter: { notKeyword: '$seen' } })
        if (res.emailIds.length > 0) {
          emailIds = [...emailIds, ...res.emailIds]
        }
        position += limit
      } while (res.emailIds.length === limit)

      console.log(' -- markAllasRead emailIds : ', emailIds)

      while (emailIds.length > 0) {
        let ids = []
        if (emailIds.length > limit) {
          ids = emailIds.splice(-limit)
        } else {
          ids = emailIds.splice(0)
        }

        res = await this.setKeywordByEmailIds(ids, '$seen', true)
        // if (res != null) {
        //   const [ updatedEmail ]= res
        //   if (updatedEmail != null) {
        //     const { updated } = updatedEmail;
        //   }
        // }
      }

      this.dataMgr.reqMailBox().catch((err: unknown) => handleJmapCallError('markAllAsRead', err))
      // real mailbox는 markAllasRead 완료 후에 새로 페이지를 로딩할 필요없음. - read 상태만 갱신하면 됨.
      if (this.isRealMailbox !== true) {
        this.resetData()
        this.reqNextThreadList()
      }

      // resultObj.close()

      return emailIds
    } catch (err) {
      console.log('markAllasRead error : ', err)
      // resultObj.close()
      throw err
    }
  }

  importMessage = async (
    emlFiles: File[],
    onProgress: (progress: number, count: number, total: number) => void,
  ) => {
    const getBuffer = async (emlFile: File) => {
      return new Promise((resolve, reject) => {
        var reader = new FileReader()

        reader.onloadend = function (e) {
          resolve(e.target.result)
        }
        reader.onerror = function (e) {
          reject(e)
        }
        reader.readAsArrayBuffer(emlFile)
      })
    }

    try {
      const promises = emlFiles.map(async (emlFile) => {
        const buffer: any = await getBuffer(emlFile)
        const postalMimeEML = await PostalMime.parse(buffer)
        // const { messageId } = postalMimeEML
        // if (messageId != null) {
        //   const msgId = getMessageId(messageId)
        // }
        return { postalMimeEML, emlFile }
      })

      const postalMimeEMLList = await Promise.all(promises)

      const validMessages = []
      const invalidMessages = []
      postalMimeEMLList.forEach(({ postalMimeEML, emlFile }) => {
        if (postalMimeEML.messageId == null) {
          invalidMessages.push(emlFile)
        } else {
          validMessages.push(emlFile)
        }
      })

      // invalidMessages가 있는 경우, 어떤 식으로 출력할지 확인 필요함.

      if (validMessages.length > 0) {
        const { emailList, errors } = await importEML(validMessages, this.getId(), null, onProgress) //, this.getRole() === 'drafts', false)
        console.log(' -- emailList : ', emailList)

        const notiInfo = {}
        if (emailList.list != null && emailList.list.length > 0) {
          emailList.list.forEach((emailData) => {
            const mailboxKeys = Object.keys(emailData.mailboxIds)
            if (mailboxKeys.length > 0) {
              mailboxKeys.forEach((mailboxId) => {
                const mailbox = this.dataMgr.getMailboxById(mailboxId)
                if (mailbox != null) {
                  mailbox.addEmailData(emailData, notiInfo, false)
                }
              })
            } else {
              this.dataMgr.applyEmailData(emailData, notiInfo, false)
            }
          })

          const eventList = this.dataMgr.makeEvent0(notiInfo)
          const notiEvent = eventList.filter((item) => {
            return item.type === TYPE.THREAD || item.type === TYPE.EMAIL
          })

          this.dataMgr.notifyThreadChanged(notiEvent)
          this.dataMgr.notifyRelatedMailboxes(notiEvent)
        }

        if (invalidMessages.length > 0) {
          if (errors != null && errors.length > 0) {
            const invalidError = errors.find((error: any) => error.type === 'invalidFiles')
            if (invalidError != null) {
              invalidError.invalidFiles.push(...invalidMessages)
            } else {
              const error: any = new Error(`import eml error - invalidFiles`)
              error.type = 'invalidFiles'
              error.invalidFiles = [...invalidMessages]
              errors.push(error)
            }
          }
        }

        return { emailList, errors }
      }

      const error: any = new Error(`import eml error - invalidFiles`)
      error.type = 'invalidFiles'
      error.invalidFiles = [...invalidMessages]

      return {
        emailList: [],
        errors: [error],
      }
    } catch (err: any) {
      console.log('importMessage error : ', err)
    }
  }

  applyEmailDeletedFromServer = (emailIdList: any, notiInfo: any) => {
    if (emailIdList != null && emailIdList.length > 0) {
      emailIdList.forEach((emailId) => {
        this.removeEmailData({ id: emailId }, notiInfo, true)
      })
    }
  }

  getJmapFilter = (): any => {
    const conditions = [
      {
        inMailbox: this.getId(),
      },
    ]
    if ((conditions as any).length > 1) {
      const filter = {
        conditions,
        operator: 'AND',
      }
      return filter as any
    }
    return conditions[0] as any
  }

  getResultFilter = () => {
    const role = this.getRole()
    const mailboxIds = []
    if (role !== 'trash' && role !== 'junk' /*  && role !== 'drafts' */) {
      const junkMailbox = this.dataMgr.getMailboxForRole('junk')
      if (junkMailbox != null) {
        mailboxIds.push(junkMailbox.getId())
      }
      const trashMailbox = this.dataMgr.getMailboxForRole('trash')
      if (trashMailbox != null) {
        mailboxIds.push(trashMailbox.getId())
      }
      // const draftMailbox = this.dataMgr.getMailboxForRole('drafts')
      // if (draftMailbox != null) {
      //   mailboxIds.push(draftMailbox.getId())
      // }
    }
    const filterFunc = (emailData: any) => {
      const isInvalidMailbox = mailboxIds.some((id) => emailData.mailboxIds[id] === true)
      return (
        isInvalidMailbox !== true &&
        (this.isEmailType === false || emailData.mailboxIds[this.getId()] === true)
      )
    }
    return filterFunc
  }

  // 메일 발송, 삭제 등 이후에 변경된 내용을 업데이트 요청.
  reqUpdatedThreadData = (isSave: any = false) => {
    const lastState = this.getLastState()
    const lastQueryState = this.getLastQueryState()

    // console.log(' --- requestUpdatedThreadQuery - ', lastState, lastQueryState)

    const listType = this.getListType()

    let clonedDelayedDataMap = null
    if (this.isRealMailbox !== true) {
      // OFM-210: 일반 sync에서는 지연 큐를 비우지 않고 보존한다.
      // 명시적 제거(메일함 전환/새로고침)는 flushDelayedData()에서만 수행.
      clonedDelayedDataMap = clone(this.delayedDataMap)
    }

    return new Promise((resolve) => {
      if (lastState == null) {
        // lastState가 없으면 새로 로딩해야 함.
        // OFM-210: 전체 재로딩 시에는 보존할 의미가 없으므로 지연 큐를 정리한다.
        if (this.isRealMailbox !== true) {
          this.clearDelayedData()
        }
        this.notifyReload()
        resolve(null)
        return
      }

      requestUpdatedThreadQuery(this, {
        maxChanges: this.maxChanges,
        sinceState: lastState,
        sinceQueryState: lastQueryState,
        type: listType,
      })
        .then(
          ({
            emailChanges,
            createdEmails,
            updatedEmails,
            destroyedEmails,
            emailQueryChanges,
            addedEmails,
            deletedEmails,
            info,
          }) => {
            console.log(
              ' -- requestUpdatedThreadQuery : ',
              emailChanges,
              emailQueryChanges,
              addedEmails,
              deletedEmails,
              createdEmails,
              updatedEmails,
              destroyedEmails,
              info,
            )

            if (info.hasMoreChanges === true) {
              setTimeout(() => {
                this.reqUpdatedThreadData(isSave)
              }, 100)
            }

            if (info.lastState != null) {
              this.setLastState(info.lastState)
              // 현재 보여지고 있는 mailbox의 lastState를 dataMgr에 설정.
              this.dataMgr.setLastState(info.lastState)
            }
            if (info.lastQueryState) {
              this.setLastQueryState(info.lastQueryState)
            }

            const notiInfo = {}

            if (isSave === true) {
              this.dataMgr.addEvent0Obj(
                notiInfo,
                TYPE.EMAIL,
                ACTION.SAVE,
                { type: TYPE.MAILBOX, id: this.getId() },
                this,
              )
            }

            const checkList = []

            const addList = []
            const updateList = []
            let deleteList = []

            const resultFilter = this.getResultFilter()

            if (emailQueryChanges != null) {
              // added와 deleted에 동시에 포함된 이메일은 mailboxIds를 이용하여 최종 check.
              deleteList = [
                // emailChanges의 정보는 filter에 상관없이 변경된 모든 이메일이 포함하므로 결과에 사용하지 않음.
                // ...deletedEmails.list.filter((emailData) => {
                //   return emailChanges.updated.indexOf(emailData.id) < 0
                // }),
                // 서버에서 이미 지워진 이메일은 정보가 없으므로, notFound로 전달됨.
                // 그러나 client에서는 정보가 남아있으므로 찾아서 지워야 함.
                ...deletedEmails.notFound.map((emailId) => {
                  return { id: emailId }
                }),
              ]

              // deletedEmails의 데이터가 이상하게 전달됨.
              // mailboxId로 확인하여 현재 메일박스에 있는 것이면 추가, 없으면 제거로 처리.
              for (var i = 0; i < deletedEmails.list.length; i += 1) {
                const emailData = deletedEmails.list[i]
                if (emailData.mailboxIds[this.getId()] === true) {
                  addList.push(emailData)
                } else {
                  deleteList.push(emailData)
                }
              }
              // .filter((emailData) => {
              //   // draft save, update 시 added와 deleted에 동일한 ID가 전달됨.
              //   // 이 경우, added에 있는 것을 우선 처리하고, deleted는 제외함.
              //   // @todo (문제 발생 시 추가 수정 필요.)
              //   return (addedEmails.list.findIndex((emailData2) => {
              //     return emailData.id === emailData2.id
              //   }) === -1)
              // });

              for (var i = 0; i < addedEmails.list.length; i += 1) {
                const emailData = addedEmails.list[i]
                // emailChanges의 정보는 filter에 상관없이 변경된 모든 이메일이 포함하므로 결과에 사용하지 않음.
                // const updateIdx = emailChanges.updated.indexOf(emailData.id)
                // if (updateIdx > -1) {
                //   updateList.push(emailData)
                // } else {
                const idx = deleteList.findIndex((emailData2) => {
                  return emailData.id === emailData2.id
                })
                if (idx < 0) {
                  addList.push(emailData)
                } else {
                  checkList.push(emailData)
                  deleteList.splice(idx, 1)
                }
                // }
              }

              checkList.forEach((emailData) => {
                if (resultFilter(emailData) === true) {
                  updateList.push(emailData)
                } else {
                  deleteList.push(emailData)
                }
              })
            } else {
              createdEmails.list.forEach((emailData) => {
                if (resultFilter(emailData) === true) {
                  addList.push(emailData)
                }
              })
              updatedEmails.list.forEach((emailData) => {
                if (resultFilter(emailData) === true) {
                  addList.push(emailData)
                } else {
                  // 변경된 이메일 중 결과에 포함되지 않았으나, 이전에 포함되어 있던 메일은 삭제 처리.
                  const isExist = this.getEmailIdx(emailData) > -1
                  if (isExist === true) {
                    deleteList.push(emailData)
                  }
                }
              })
              destroyedEmails.list.forEach((emailData) => {
                if (resultFilter(emailData) === true) {
                  deleteList.push(emailData)
                }
              })
              destroyedEmails.notFound.forEach((emailId) => {
                const emailObj = this.getEmailById(emailId)
                if (
                  emailObj != null &&
                  emailObj.data != null &&
                  resultFilter(emailObj.data) === true
                ) {
                  deleteList.push(emailObj.data)
                }
              })
            }

            if (this.isRealMailbox !== true) {
              // OFM-210: 현재 메일함을 보는 동안 읽힌/플래그 해제된 메일은 즉시 제거하지 않고
              // 보존한다. 읽음은 서버 sync로 updatedEmails/checkList에 실려 돌아와 resultFilter
              // 미통과로 deleteList에 분류되는데, "메일박스에 여전히 존재(mailboxFilter 통과)"하면
              // 단지 읽힘 탈락일 뿐 실제 이동/삭제가 아니므로 deleteList에서 제외해 보존한다.
              // delayedDataMap 등록은 setKeyword의 jamUpdate.then(비동기)에서 일어나 진입부
              // clone 시점에 누락될 수 있으므로, 타이밍에 의존하지 않도록 mailboxFilter로 직접
              // 판정한다(삭제 직후/hasMoreChanges 재귀 reqUpdatedThreadData의 race 방지).
              // 실제 제거는 flushDelayedData()(메일함 전환/새로고침)에서만 수행한다.
              const mailboxFilter = this.getMailboxFilter()

              deleteList = deleteList.filter((emailData) => {
                // mailboxIds 정보가 없으면(notFound 등 서버에서 실제 삭제됨) 그대로 제거.
                if (emailData.mailboxIds != null && mailboxFilter(emailData) === true) {
                  // 메일박스에 잔존하면 제거하지 않는다(읽음/안읽음 무관 — read-vanish 방지).
                  // 단, 지연 큐(delayedDataMap)에는 "읽음/플래그 해제로 필터 탈락한" 메일만
                  // 등록한다. 안읽음 메일은 getResultFilter 통과로 getAllData가 이미 유지하므로
                  // 등록이 불필요하며, 등록 시 지연 큐가 부풀어 flush 동작에 영향을 준다.
                  if (resultFilter(emailData) !== true) {
                    const emailObj = this.getEmailById(emailData.id)
                    if (emailObj != null) {
                      this.delayedDataMap[emailData.id] = emailObj
                    }
                  }
                  return false
                }
                return true
              })

              // 서버 변경에 없는 기존 지연 보존 메일도 리스트에서 빠지지 않도록 다시 반영한다.
              const delayedKeepList = { ...clonedDelayedDataMap }
              const handledData = [...addList, ...updateList, ...deleteList]
              handledData.forEach((emailData) => {
                delete delayedKeepList[emailData.id]
              })

              Object.keys(delayedKeepList).forEach((emailId) => {
                const emailObj = delayedKeepList[emailId]
                // mailboxIds 정보가 없으면(파괴 등으로 데이터 소실) 보존하지 않는다.
                // (deleteList.filter 경로와 동일하게 mailboxFilter 호출 전 null 가드)
                if (emailObj.data.mailboxIds == null || mailboxFilter(emailObj.data) !== true) {
                  // 메일박스를 떠난(이동/삭제) 메일은 보존하지 않는다.
                  return
                }
                if (resultFilter(emailObj.data) === true) {
                  // 여전히 안읽음 → 리스트 유지/추가
                  addList.push(emailObj.data)
                } else {
                  // 읽혔으나 보존 → 읽음 데이터로 갱신만 하고 제거하지 않음
                  updateList.push(emailObj.data)
                }
              })
            }

            // if (this.isEmailType !== true) { // mailbox가 thread type인 경우.
            //   // Thread가 없는 상태에서 추가되는 email인 경우에는, thread 정보를 새로 요청하여 추가한다.
            //   const threadIdMap = {};
            //   [...addList, ...updateList].forEach((emailData) => {
            //     const threadId = emailData.threadId;
            //     const idx = this.threadList.findIndex((threadData) => {
            //       return threadData.data.id === threadId
            //     })
            //     if (idx < 0 && threadIdMap[threadId] == null) {
            //       threadIdMap[threadId] = emailData;
            //     }
            //   });

            //   Object.keys(threadIdMap).forEach((threadId) => {
            //     const emailData = threadIdMap[threadId];
            //     this.reqOneThread(emailData.id);
            //   })
            // }

            if (addList.length > 0) {
              addList.forEach((emailData) => {
                const emailObj = this.addEmailData(emailData, notiInfo, false)
                if (this.isRealMailbox !== true) {
                  this.dataMgr.addEvent0Obj(
                    notiInfo,
                    TYPE.EMAIL,
                    ACTION.ADD,
                    { type: TYPE.MAILBOX, id: this.getId() },
                    emailObj,
                  )
                }
              })
            }

            if (updateList.length > 0) {
              updateList.forEach((emailData) => {
                const isExist = this.getEmailIdx(emailData) > -1
                const emailObj = this.addEmailData(emailData, notiInfo, false)
                if (this.isRealMailbox !== true) {
                  this.dataMgr.addEvent0Obj(
                    notiInfo,
                    TYPE.EMAIL,
                    isExist === true ? ACTION.UPDATE : ACTION.ADD,
                    { type: TYPE.MAILBOX, id: this.getId() },
                    emailObj,
                  )
                }
              })
            }

            if (deleteList.length > 0) {
              deleteList.forEach((emailData) => {
                const emailObj = this.removeEmailData(emailData, notiInfo)
                if (this.isRealMailbox !== true) {
                  this.dataMgr.addEvent0Obj(
                    notiInfo,
                    TYPE.EMAIL,
                    ACTION.DELETE,
                    { type: TYPE.MAILBOX, id: this.getId() },
                    emailObj,
                  )
                }
              })
            }

            this.sortEmail()
            this.sortThread()

            const eventList = this.dataMgr.makeEvent0(notiInfo)

            const notiEvent = eventList.filter((item) => {
              return item.type === TYPE.THREAD || item.type === TYPE.EMAIL
            })

            this.dataMgr.notifyThreadChanged(notiEvent)

            this.dataMgr.notifyRelatedMailboxes(notiEvent)

            resolve(true)
          },
        )
        .catch((err) => {
          console.log(' -- reqUpdatedThreadData error : ', err)
          if (Array.isArray(err) && err.length > 0) {
            err.some((errObj) => {
              if (errObj.type === 'tooManyChanges') {
                this.enableQueryChange = false

                setTimeout(() => {
                  this.reqUpdatedThreadData(isSave)
                }, 100)
                return true
              } else if (errObj.type === 'cannotCalculateChanges') {
                // 전체 재로딩 경로이므로 지연 큐를 정리한다(lastState==null 경로와 일관).
                if (this.isRealMailbox !== true) {
                  this.clearDelayedData()
                }
                this.notifyReload()
                return true
              }

              return false
            })
          }
          resolve(null)
        })
    })

    return Promise.resolve(null)
  }

  getListType = () => {
    const isThread = settings.getValue('email').mailUseThread
    const role = this.getRole()
    return role === 'drafts' || isThread !== true ? TYPE.EMAIL : TYPE.THREAD
  }

  reqNextThreadList = (offset?: number, info?: any, dataInfo?: any, needTotal?: boolean) => {
    console.log(' -- reqNextThreadList : ', this)
    if (offset != null) {
      this.position = offset
    } else if (this.isFirstLoading === true) {
      this.position = 0
      // OMD-21: 새 검색 목록 로딩 시작 — 매치 emailId 누적 집합 초기화.
      this.searchMatchedIds.clear()
    } else if (this.hasMoreData === true) {
      if (this.searchMode === true && emailSearchProvider === 'OPEN_SEARCH') {
        // OMD-21: OpenSearch 검색(EMAIL/THREAD 공통)의 다음 offset 은 "소비한 매치 emailId
        // 수"이다. 이 값은 직전 응답의 resolve 에서 searchMatchedIds 가 완전히 갱신된 뒤
        // this.position 에 확정해 둔다. 요청 시점에 재계산하지 않는 이유: searchMatchedIds 가
        // 아직 완전히 채워지기 전(fake 2단계 로딩·재검색 churn 도중)에 읽으면 1건 undercount
        // 되어 offset 이 밀리고(0→19→39...), 중복 수신→dedup→표시 누락(75/76)을 유발했다.
        // 단일 writer(resolve) 시점에 확정한 값을 그대로 사용해 읽기 레이스를 제거한다.
      } else if (this.useLocalPositionCalc() === true) {
        // OFM-210/225: 이동·읽음으로 서버 목록이 당겨질 때 절대 offset이 어긋나므로
        // "보유 항목 중 여전히 서버 리스트에 남는(필터 통과) 수"로 재계산한다.
        const resultFilter = this.getResultFilter()
        const sourceList = this.isEmailType === true ? this.emailListForEmail : this.emailList
        const isUnreadMailbox = this.getRole() === 'unread'
        this.position = sourceList.filter((emailObj: any) => {
          if (
            isUnreadMailbox === true &&
            typeof emailObj.isRead === 'function' &&
            emailObj.isRead() === true
          ) {
            return false
          }
          return resultFilter(emailObj.data)
        }).length
      } else {
        this.position += this.limit
      }
    } else {
      return Promise.resolve(null)
    }

    const listType = this.getListType()

    const options: any = {
      position: this.position,
      limit: this.limit,
      text: this.searchText,
      type: listType,
      needTotal: needTotal === true ? true : false,
    }

    if (info != null) {
      const { filters, sorts } = info
      if (filters != null && filters.length > 0) {
        options.filters = filters
      }
      if (sorts != null && sorts.length > 0) {
        options.sorts = sorts
      }
    }

    // OMD-107: 요청 발사 직전의 세대를 캡처한다. 응답 도착 시 세대가 바뀌었다면(삭제/검색/정렬
    // 변경으로 resetReqData가 실행됨) 이 응답은 stale이므로 폐기한다.
    const capturedGeneration = this.reqGeneration

    return new Promise((resolve) => {
      requestThread(this, options)
        .then(({ emailIds, emailsForThread, threadList, allEmailList, total, info }) => {
          if (capturedGeneration !== this.reqGeneration) {
            // 리셋(삭제/검색/정렬 변경)과 교차한 stale 응답 — 전체 폐기한다. 부분 반영은
            // 페이지네이션/state/데이터 간 불일치를 만들어 무한 스크롤 정지를 유발한다.
            // resolve(null)로 기존 콜러의 null 계약(무시)을 그대로 활용한다.
            console.log(
              ' -- reqNextThreadList: stale response dropped (gen',
              capturedGeneration,
              '->',
              this.reqGeneration,
              ')',
            )
            addStaleThreadListDropBreadcrumb(capturedGeneration, this.reqGeneration)
            resolve(null)
            return
          }

          console.log(' -- reqNextThreadList : ', emailIds, threadList, allEmailList, total)

          if (info.lastState != null) {
            this.setLastState(info.lastState)
            // 현재 보여지고 있는 mailbox의 lastState를 dataMgr에 설정.
            this.dataMgr.setLastState(info.lastState)
          }
          if (info.lastQueryState) {
            this.setLastQueryState(info.lastQueryState)
          }

          if (info.enableQueryChange === true && this.enableQueryChange == null) {
            this.enableQueryChange = true
          }

          this.isFirstLoading = false

          this.hasMoreData = emailIds.length === this.limit ? true : false

          // OMD-21: 검색 모드에서 이번 페이지의 매치 emailId 를 누적하고, 다음 페이지 offset(position)을
          // 이 시점(단일 writer)에서 확정한다. 소비한 매치 수(이동/삭제 = resultFilter 탈락분 제외)로 계산.
          // 요청 시점 재계산을 없애 searchMatchedIds 미완성 상태 읽기로 인한 offset undercount(간헐적
          // 0→19→39 드리프트 → 75/76 표시 누락)를 제거한다.
          if (this.searchMode === true && emailSearchProvider === 'OPEN_SEARCH') {
            emailIds.forEach((matchedId: string) => this.searchMatchedIds.add(matchedId))

            const searchResultFilter = this.getResultFilter()
            this.position = countConsumedMatches(
              this.searchMatchedIds,
              (id) => searchResultFilter({ id }) !== true,
            )
          }

          const resultFilter = this.getResultFilter()

          const notiInfo = null

          let threadIdMap = null

          // reqNextThreadList에서는 순서대로 데이터를 로딩하므로, 이미 존재하는 것은 정상적으로 로딩된 것이 아님.
          // 그러므로 emailList에서 제거하고, 순서대로 추가되도록 해야 함.
          this.emailList = this.emailList.filter((emailObj) => {
            return emailIds.indexOf(emailObj.getId()) < 0
          })

          if (emailsForThread != null) {
            threadIdMap = {}
            emailsForThread.forEach((data) => {
              const pos = this.threadList.findIndex((threadObj) => {
                return threadObj.getId() === data.threadId
              })
              if (pos > -1) {
                this.threadList.splice(pos, 1)

                const noLinkedEmailList = this.emailList.filter((emailObj) => {
                  if (emailObj.data.threadId === data.threadId) {
                    return false
                  }
                  return true
                })
                this.emailList = [...noLinkedEmailList]
              }

              threadIdMap[data.threadId] = data
            })
          }

          if (this.isEmailType === true) {
            allEmailList.forEach((emailData) => {
              let pos = this.emailListForEmail.findIndex((emailObj) => {
                return emailObj.getId() === emailData.id
              })
              if (pos > -1) {
                this.emailListForEmail.splice(pos, 1)

                pos = this.emailList.findIndex((emailObj) => {
                  return emailObj.getId() === emailData.id
                })
                if (pos > -1) {
                  this.emailList.splice(pos, 1)
                }
              }
            })
          }

          if (allEmailList != null) {
            allEmailList.filter(resultFilter).forEach((emailData) => {
              if (emailData.messageId == null) {
                // undefined 값 키는 넣지 않는다 — applyData가 undefined도 그대로 병합해
                // 이미 로드된 메일의 실데이터(threadId/keywords)를 지울 수 있다.
                const fakeEmailData: any = {
                  id: emailData.id,
                  mailboxIds: emailData.mailboxIds || {},
                  extra: {
                    isFake: true,
                    mailboxObj: this,
                  },
                }
                if (emailData.threadId != null) {
                  fakeEmailData.threadId = emailData.threadId
                }
                if (emailData.keywords != null) {
                  fakeEmailData.keywords = emailData.keywords
                }
                if (emailData.receivedAt != null) {
                  fakeEmailData.receivedAt = emailData.receivedAt
                }
                if (emailData.extra != null && emailData.extra.isOpenSearch === true) {
                  // OFM-225: 검색 유래 표시 — applyData에서 캐시 실데이터 보존 및
                  // id만으로의 최소 재조회에 사용.
                  fakeEmailData.extra.isOpenSearch = true
                }
                if (threadList != null) {
                  const threadData = threadList.find((threadData) => {
                    return threadData.emailIds.indexOf(emailData.id) > -1
                  })
                  fakeEmailData.threadId = threadData.id
                }
                this.addEmailData(fakeEmailData, notiInfo, false)
              } else {
                this.addEmailData(emailData, notiInfo, false)
              }
            })
          }
          if (threadList != null) {
            threadList.forEach((threadData) => {
              if (allEmailList == null) {
                threadData.emailIds.forEach((emailId) => {
                  const fakeEmailData = {
                    id: emailId,
                    threadId: threadData.id,
                    mailboxIds: {},
                    extra: {
                      isFake: true,
                      mailboxObj: this,
                    },
                  }
                  this.addEmailData(fakeEmailData, notiInfo, false)
                })
              }
              const threadObj = this.addThreadData(threadData, notiInfo, false)
              if (threadIdMap != null) {
                const { id } = threadIdMap[threadObj.getId()]
                if (id != null) {
                  const titleEmail = this.getEmailById(id)
                  if (titleEmail != null) {
                    threadObj.titleEmail = titleEmail
                    threadObj.compEmail = titleEmail
                  }
                }
              }
            })
          }

          // 리스트 요청을 통해, 마지막으로 추가된 emailId를 저장.
          // 이 이메일의 정보를 이용하여 threadList에서 표시될 이메일을 결정한다.
          // - 정렬 순서상 이 이메일보다 나중 위치에 있는 것은 미리 로딩되더라도 표시하지 않는다.
          if (this.hasMoreData === true) {
            this.limitEmailId = emailIds[emailIds.length - 1]
          } else {
            // 모두 로딩되면 limitEmailId를 초기화.
            this.limitEmailId = null
          }

          if (dataInfo != null) {
            const { linkedEmailIds, linkedEmailIdsLoaded } = dataInfo
            if (linkedEmailIds != null && linkedEmailIds.length > 0) {
              emailIds.forEach((emailId) => {
                const pos = linkedEmailIds.indexOf(emailId)
                if (pos > -1) {
                  linkedEmailIds.splice(pos, 1)
                  linkedEmailIdsLoaded() // threadList에서 linkedEmailIds가 로딩되었음을 알림.
                }
              })

              linkedEmailIds.forEach((emailId) => {
                if (this.isEmailType === true) {
                  let pos = this.emailListForEmail.findIndex((emailObj) => {
                    return emailObj.getId() === emailId
                  })
                  if (pos > -1) {
                    const [emailObj] = this.emailListForEmail.splice(pos, 1)
                    this.emailListForEmail.push(emailObj)

                    pos = this.emailList.findIndex((emailObj) => {
                      return emailObj.getId() === emailId
                    })
                    if (pos > -1) {
                      const [emailObj] = this.emailList.splice(pos, 1)
                      this.emailList.push(emailObj)
                    }
                  }
                } else {
                  let pos = this.threadList.findIndex((threadObj) => {
                    return threadObj.data.emailIds.indexOf(emailId) > -1
                  })
                  if (pos > -1) {
                    const [threadObj] = this.threadList.splice(pos, 1)
                    this.threadList.push(threadObj)
                  }

                  const emailObj = this.getEmailById(emailId)
                  const threadObj = this.getThreadById(emailObj.data.threadId)
                  if (threadObj != null) {
                    const tmpList = []
                    const noLinkedEmailList = this.emailList.filter((emailObj) => {
                      if (emailObj.data.threadId === threadObj.getId()) {
                        tmpList.push(emailObj)
                        return false
                      }
                      return true
                    })
                    this.emailList = [...noLinkedEmailList, ...tmpList]
                  }
                }
              })
            }
          }

          this.threadList = [...this.threadList]
          this.emailList = [...this.emailList]

          this.emailListForEmail = [...this.emailListForEmail]

          this.sortEmail()
          this.sortThread()

          // const eventList = this.dataMgr.makeEvent0(notiInfo)

          // const notiEvent = eventList.filter((item) => {
          //   return item.type === TYPE.THREAD || item.type === TYPE.EMAIL
          // })

          // this.dataMgr.notifyThreadChanged(notiEvent)
          // const selfNoti = notiEvent.filter((event) => {
          //   return event.targetType === TYPE.MAILBOX && event.target.data.id === this.data.id
          // })
          // this.threadChangeListener.notify(selfNoti)

          this.notifyChangeAll()

          const hasMore = this.hasMoreData

          resolve({ emailIds, threadList, allEmailList, total, hasMore })
        })
        .catch((err) => {
          console.log(' -- reqNextThreadList error : ', err)
          // OFM-212: OpenSearch 검색 요청 실패(5xx/네트워크 등)는 아래에서 resolve(null)로
          // 삼켜져 리스트가 조용히 비어 사용자 피드백이 없다. 검색 모드에서는 오류를 토스트로 알린다.
          // (401은 전역 토큰 만료 핸들러가 재로그인으로 처리하므로 중복 표시하지 않는다.)
          const errStatus = (err as { status?: number } | null)?.status
          if (
            this.searchMode === true &&
            emailSearchProvider === 'OPEN_SEARCH' &&
            errStatus !== 401
          ) {
            // 고정 key로 중복 오류 토스트가 쌓이지 않고 하나로 대체되게 한다.
            showResult({ key: 'search-error', message: i18n.t('email.desc.searchError') })
          }
          if (Array.isArray(err) && err.length > 0) {
            err.some((errObj) => {
              // 삭제된 mailbox에 대한 처리.(자신이 삭제됨.)
              if (
                errObj.type === 'invalidArguments' &&
                errObj.arguments.indexOf('filter/inMailbox') > -1
              ) {
                setTimeout(() => {
                  this.dataMgr.notifyMailboxDeleted(this)
                }, 1)
                return true
              }
              return false
            })
          }
          resolve(null)
        })
    })
  }

  reqOneThread = (emailId: any) => {
    return requestOneThread(emailId).then(({ threadList, emailList }: any) => {
      console.log(' -- reqOneThread : ', threadList, emailList)

      const resultFilter = this.getResultFilter()

      const notiInfo = null

      emailList.filter(resultFilter).forEach((emailData) => {
        this.addEmailData(emailData, notiInfo, false)
      })

      if (threadList != null) {
        threadList.forEach((threadData) => {
          this.addThreadData(threadData, notiInfo, false)
        })
      }

      this.threadList = [...this.threadList]
      this.emailList = [...this.emailList]

      this.emailListForEmail = [...this.emailListForEmail]

      this.sortEmail()
      this.sortThread()

      this.notifyChangeAll()

      return { threadList, emailList }
    })
  }

  setSortInfo = (sortInfo: any) => {
    this.sortInfo = sortInfo
    const curSortInfo = this.getSortInfo()
    this.sortFunc = createSortFunc(curSortInfo)
  }

  getSortFunc: () => any = () => {
    return this.sortFunc
  }

  getLimitEmail: () => EmailObj | null = () => {
    if (this.limitEmailId != null) {
      return this.getEmailById(this.limitEmailId)
    }
    return null
  }

  sortEmail = () => {
    // const stopSort = this.emailList.some((emailObj) => {
    //   if (emailObj == null || emailObj.data.messageId == null) {
    //     return true
    //   }
    //   return false
    // })

    // if (stopSort === true) {
    //   if (this.sortEmailTimer != null) {
    //     clearTimeout(this.sortEmailTimer)
    //   }
    //   this.sortEmailTimer = setTimeout(() => {
    //     this.sortEmail()
    //   }, 200)
    //   return
    // } else {
    //   if (this.sortEmailTimer != null) {
    //     clearTimeout(this.sortEmailTimer)
    //   }
    //   this.sortEmailTimer = null
    // }

    this.emailList.sort((a, b) => {
      return this.sortFunc(a, b)
    })
    if (this.isEmailType === true) {
      this.emailListForEmail.sort((a, b) => {
        return this.sortFunc(a, b)
      })
    }
  }

  sortThread = () => {
    let removeItem = null

    do {
      removeItem = this.threadList.find((item: any) => {
        return item.data.emailIds.length === 0
      })

      if (removeItem != null) {
        this.removeThread_no_noti(removeItem)
        // this.dataMgr.removeThreadById(removeItem.data.id);
      }
    } while (removeItem != null)

    const needCheckMailbox = this.isRealMailbox === true && this.getRole() !== 'all'

    this.threadList.forEach((threadObj) => {
      return this.processThreadObj(threadObj, needCheckMailbox)
    })

    // if (stopSort === true) {
    //   if (this.sortThreadTimer != null) {
    //     clearTimeout(this.sortThreadTimer)
    //   }
    //   this.sortThreadTimer = setTimeout(() => {
    //     this.sortThread()
    //   }, 200)
    //   return
    // } else {
    //   if (this.sortThreadTimer != null) {
    //     clearTimeout(this.sortThreadTimer)
    //   }
    //   this.sortThreadTimer = null
    // }

    // this.threadList.forEach((threadObj, idx) => {
    //   console.log(' -- prev sort test : ', idx, threadObj.compEmail.data.from[0] )
    // })

    this.threadList.sort((aObj, bObj) => {
      // if (aObj.data.emailIds.length === 0 || bObj.data.emailIds.length === 0) {
      //   return 0;
      // }
      const a = aObj.compEmail
      const b = bObj.compEmail

      return this.sortFunc(a, b)
    })

    // this.threadList.forEach((threadObj, idx) => {
    //   console.log(' -- sort test : ', idx, threadObj.compEmail.data.from[0] )
    // })
  }

  processThreadObj = (threadObj: any, needCheckMailbox: boolean): boolean => {
    let isValidValue = true
    let tmpEmails = threadObj.data.emailIds
      .map((emailId) => {
        const emailObj = this.getEmailById(emailId)
        if (checkEmailValid(emailObj) === false) {
          isValidValue = false
        }
        return emailObj
      })
      .filter((emailObj) => {
        if (needCheckMailbox === true) {
          return this.isContain(emailObj) === true
        }
        return true
      })
      .sort(this.sortFunc)

    if (isValidValue === true) {
      threadObj.compEmail = tmpEmails[0]
    }
    return isValidValue
  }

  checkRelatedMailboxes = (emailObj: any, notiInfo: any) => {
    // 이메일이 포함된 thread가 관계된 모든 mailbox에 update 이벤트 전달.
    if (emailObj != null) {
      const emailData = emailObj.data != null ? emailObj.data : emailObj
      const thread = this.dataMgr.getThreadById(emailData.threadId)
      thread.data.emailIds.forEach((emailId) => {
        if (emailId !== emailObj.getId()) {
          const childEmail = this.dataMgr.getEmailById(emailId)
          if (childEmail != null) {
            Object.keys(childEmail.data.mailboxIds).forEach((mailboxId) => {
              const excludeCheck = emailData.mailboxIds[mailboxId] === true
              if (excludeCheck === false) {
                this.dataMgr.addEvent0Obj(
                  notiInfo,
                  TYPE.THREAD,
                  ACTION.UPDATE,
                  { type: TYPE.MAILBOX, id: mailboxId },
                  thread,
                )
              }
            })
          }
        }
      })
    }
  }

  // mailbox에 상관없이 message 자체에 포함된 mailboxIds에서 toMailbox로 이동.
  move = (messages: any, toMailBox: any, isRestore: any, useResultUI: any) => {
    let toId = null
    let toExist = false
    let defaultFromId = null

    const mailboxIds = {}
    const mailboxMap = {}

    const onlyOneMailbox = Array.isArray(toMailBox) === false

    if (onlyOneMailbox === true) {
      toId = toMailBox.getId()

      toExist = this.dataMgr.getMailboxById(toId) != null

      if (toExist !== true) {
        throw new Error('toMailBox is not exist.')
      }

      defaultFromId = this.getId()
      mailboxIds[defaultFromId] = true
      mailboxIds[toId] = true
    }

    const undoList = []

    const data = []
    messages.forEach((email, idx) => {
      const { data: emailData } = email

      let fromId = emailData.mailboxIds[defaultFromId] === true ? defaultFromId : null
      if (fromId == null) {
        // 현재 mailbox에 포함된 email이 아니므로 첫번째 mailboxId를 from으로 사용.
        fromId = Object.keys(emailData.mailboxIds)[0]
        // 다른 mailbox의 email이 처리되는 것을 확인하기 위해 로그 출력.
        console.log(' -- move from different mailbox : ', fromId)

        mailboxIds[fromId] = true
      }

      if (onlyOneMailbox !== true) {
        toId = toMailBox[idx].getId()

        mailboxMap[emailData.id] = toMailBox[idx]

        toExist = this.dataMgr.getMailboxById(toId) != null

        if (toExist !== true) {
          throw new Error('toMailBox is not exist.')
        }

        mailboxIds[toId] = true
      }

      // 이동할 mailbox가 현재 mailbox와 다를 경우에만 이동 처리.
      if (fromId != null && fromId !== toId && email.isMailState(EMAIL_STATE.NORMAL)) {
        const item = {
          id: emailData.id,
          mailboxIds: { ...emailData.mailboxIds },
        }

        delete item.mailboxIds[fromId]
        item.mailboxIds[toId] = true

        email.setMailState(EMAIL_STATE.MOVING, true)

        data.push(item)
        undoList.push({
          emailObj: email,
          fromId,
        })
      }
    })

    if (data.length === 0) {
      return Promise.resolve({})
    }

    const clearMailState = () => {
      data.forEach((emailData) => {
        // 이동 완료 후 이메일 상태 변경.
        const email = this.dataMgr.getEmailById(emailData.id)
        if (email != null) {
          email.setMailState(EMAIL_STATE.MOVING, false)
        }
      })
    }

    if (this.prevResultUI != null && this.prevResultUI.obj != null) {
      this.prevResultUI.obj.close()
    }

    this.prevResultUI = {}

    const resultUI = this.prevResultUI
    if (useResultUI === true) {
      resultUI.obj = showResult({
        loading: true,
        closeOnClick: false,
        message: i18n.t('common.message.progress'),
        duration: 10, // second.
      })
    }

    // return new Promise((resolve) => {
    //   resolve(true)
    // })

    return jamUpdate('Email', data)
      .then(([res]) => {
        console.log('move : ', res)
        if (res.updated != null) {
          // let cnt = data.length

          const notiInfo = {}

          data.forEach((emailData) => {
            if (res.updated[emailData.id] !== undefined) {
              const targetMailbox = onlyOneMailbox === false ? mailboxMap[emailData.id] : toMailBox
              const emailObj = targetMailbox.addEmailData(emailData, notiInfo, false)
              // Filtered mailbox일 때 처리.
              if (this.isRealMailbox !== true && defaultFromId !== toId) {
                this.dataMgr.addEvent0Obj(
                  notiInfo,
                  TYPE.EMAIL,
                  ACTION.DELETE,
                  { type: TYPE.MAILBOX, id: defaultFromId },
                  emailObj,
                )
              }

              if (emailObj != null) {
                // 이메일이 포함된 thread가 관계된 모든 mailbox에 update 이벤트 전달.
                this.checkRelatedMailboxes(emailObj, notiInfo)
              }
            }
          })

          // 검색 모드에서 명시적으로 이동된 메일은 검색 결과에 존재하지 않는 것으로 간주해
          // 등록한다. 복원(isRestore: 휴지통 복원·실행 취소)은 해제해 다시 노출될 수 있게 한다.
          const searchMailbox = this.dataMgr.searchMailbox
          if (searchMailbox != null && searchMailbox.isSearchMode() === true) {
            const movedEmailIds = data
              .filter((emailData) => res.updated[emailData.id] !== undefined)
              .map((emailData) => emailData.id)
            if (movedEmailIds.length > 0) {
              if (isRestore === true) {
                searchMailbox.unmarkMovedEmails(movedEmailIds)
              } else {
                searchMailbox.applyMovedEmails(movedEmailIds, notiInfo)
              }
            }
          }

          Object.keys(mailboxIds).forEach((mailboxId) => {
            const mailboxObj = this.dataMgr.getMailboxById(mailboxId)
            if (mailboxObj != null) {
              mailboxObj.sortEmail()
              mailboxObj.sortThread()
            }
          })

          clearMailState()

          const eventList = this.dataMgr.makeEvent0(notiInfo)

          const notiEvent = eventList.filter((item) => {
            return item.type === TYPE.THREAD || item.type === TYPE.EMAIL
          })

          this.dataMgr.notifyThreadChanged(notiEvent)

          this.dataMgr.notifyRelatedMailboxes(notiEvent)

          const mailboxList = Object.keys(mailboxIds).map((mailboxId) => {
            return this.dataMgr.getMailboxById(mailboxId)
          })

          this.dataMgr
            .reqMailBox(mailboxList)
            .catch((err: unknown) => handleJmapCallError('moveEmail', err))

          // 이동 시 keyword 상태는 그대로 유지.
          // if (isRestore !== true) {
          //   const kdata = []
          //   messages.forEach((email) => {
          //     const item = {
          //       id: email.data.id,
          //       'keywords/$seen': null,
          //       'keywords/$flagged': null,
          //     }

          //     kdata.push(item)
          //   })

          //   jamUpdate('Email', kdata).then(([res]) => {
          //     console.log('update keyword : ', res)
          //   })
          // }
        }

        if (useResultUI === true) {
          const count = messages.length
          const name = toMailBox.getDisplayName()
          resultUI.obj = showResult({
            key: resultUI.obj.key,
            loading: false,
            closeOnClick: true,
            // n개 {foldername}(으)로 이동됨.
            message: i18n.t('email.result.move', { count, name }),
            // 실행 취소.
            actionText: i18n.t('common.button.undo'),
            action: () => {
              // 원래 폴더로 되돌리기.
              if (undoList != null && undoList.length > 0) {
                const undoMessages = []
                const undoMailboxes = []

                undoList.forEach((undoData) => {
                  const { emailObj, fromId } = undoData
                  const mailboxObj = this.dataMgr.getMailboxById(fromId, true)
                  if (mailboxObj != null) {
                    undoMessages.push(emailObj)
                    undoMailboxes.push(mailboxObj)
                  }
                })

                toMailBox.move(undoMessages, undoMailboxes, true).then(() => {
                  if (
                    this.isRealMailbox !== true ||
                    this.dataMgr.searchMailbox.isSearchMode() === true
                  ) {
                    this.reqUpdatedThreadData()
                  }
                })
              } else {
                toMailBox.move(messages, this, true).then(() => {
                  if (
                    this.isRealMailbox !== true ||
                    this.dataMgr.searchMailbox.isSearchMode() === true
                  ) {
                    this.reqUpdatedThreadData()
                  }
                })
              }
            },
          })
        }

        return res
      })
      .catch((err) => {
        console.error('move error : ', err)
        clearMailState()
      })
  }

  archive = (messages: any, useResultUI: any) => {
    const archiveFolder = this.dataMgr.getMailboxForRole('archive')
    if (archiveFolder.getId() === this.getId()) {
      return Promise.resolve(null)
    }

    return new Promise((resolve) => {
      const selectArchiveFolder = () => {
        showResult({
          message: i18n.t('threadList.message.needArchiveFolder'),
          actionText: i18n.t('common.button.settings'),
          action: () => {
            settings.open({
              selectedTabs: ['accounts', 'mailbox'],
            })
          },
          duration: 4.5,
        })
      }

      if (archiveFolder != null) {
        this.move(messages, archiveFolder, false, useResultUI)
      } else {
        selectArchiveFolder()
      }

      resolve(true)
    })
  }

  isMoveEnabled = () => {
    const role = this.getRole()
    return role !== 'drafts'
  }

  isSentMailbox = (): boolean => {
    const role = this.getRole()
    return role === 'sent'
  }
}

// 3. FilterMailbox 구현 (JMAPMailbox 상속, options 파라미터 추가)
export class FilterMailbox extends JMAPMailbox {
  options: any
  constructor(dataMgr: any, options: any) {
    super(dataMgr)
    this.options = options
    this._listType =
      options.listType == null || options.listType === TYPE.EMAIL ? TYPE.EMAIL : TYPE.THREAD
    this.isEmailType = this._listType === TYPE.EMAIL
    this.isRealMailbox = false
    this.useUnreadCnt = options.useUnreadCnt != null ? options.useUnreadCnt : true
  }

  // 실제 folder가 없으므로 아무 처리를 하지 않음.
  /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
  applyData = (data: any) => {
    return false
  }

  // createFilterMailboxObj 내에서 self.으로 선언되는 함수 중 MailboxObj와 중복되지 않는 함수만 여기에 구현
  getId = (): any => this.options.id
  getRole = (): any => this.options.role
  getDisplayName = (): any => {
    const role = this.getRole()
    /* i18next-extract-disable-next-line */
    return i18n.t(`mailbox.displayName.${role}`)
  }
  getName = (): any => this.options.name
  getFullName = (): any => this.getDisplayName()
  /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
  setRole = (role: any): any => {}
  /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
  rename = (newName: any): any => {}

  getParentMailbox = (): any => null
  isContain = (email: any): boolean => {
    const idx = this.getEmailIdx(email)
    return idx > -1
  }

  /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
  changeParent = (newParentId: any, newName?: any): any => {}
  delete = (): any => null
  getListType = (): any => this._listType
  getJmapFilter = (): any => (this.options.getJmapFilter ? this.options.getJmapFilter() : null)

  /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
  notifyCountChanged = (count: any): void => {}

  getViewCountInfo = (): { count: number | null; type: string } | null => {
    return {
      count: this.data.totalEmails,
      type: 'total',
    }
  }

  updateData = async (): Promise<any> => {
    let filters = this.getJmapFilter()
    if (this.useUnreadCnt !== false) {
      if ((filters as any).conditions != null) {
        const hasSeen = (filters as any).conditions.some(
          (condition: any) => condition.notKeyword === '$seen',
        )
        if (!hasSeen) {
          ;(filters as any).conditions.push({ notKeyword: '$seen' })
        }
      } else if ((filters as any).notKeyword !== '$seen') {
        filters = {
          conditions: [filters, { notKeyword: '$seen' }],
          operator: 'AND',
        }
      }
    }

    let result = null
    let count = null
    try {
      const { result: result0, count: count0 } = await getCount({ type: TYPE.EMAIL }, filters)
      result = result0
      count = count0
    } catch (error) {
      console.error('getCount failed : ', error)
      return null
    }

    if (result != null) {
      // count를 읽어올 때, lastState를 확인하여 mailboxContainer(mailboxList)에 전달.
      const queryState = result.queryState
      const textList = queryState.split(':')
      const lastState = textList[0]
      if (dataManager.JMAPMailboxContainer != null) {
        dataManager.JMAPMailboxContainer.notifyLastStateChanged(lastState)
      }
    }
    this.notifyCountChanged(count)
    if (count != null) {
      if (this.useUnreadCnt !== false) {
        if (this.data.unreadEmails != count) {
          this.data.unreadEmails = count
          this.dataMgr.mailbox.changeListener.notify({
            action: ACTION.UPDATE,
            type: TYPE.MAILBOX,
            data: { item: this },
          })
        }
      } else {
        if (this.data.totalEmails != count) {
          this.data.totalEmails = count
          this.dataMgr.mailbox.changeListener.notify({
            action: ACTION.UPDATE,
            type: TYPE.MAILBOX,
            data: { item: this },
          })
        }
      }
    }
    return count
  }
  getResultFilter = (): any =>
    this.options.getResultFilter ? this.options.getResultFilter() : () => true
  delayedDataMap: any = {}
  clearDelayedData = (): void => {
    this.delayedDataMap = {}
  }
  addDelayedData = (emails: any): void => {
    emails.forEach((email: any) => {
      const emailId = email.getId()
      this.delayedDataMap[emailId] = email
    })
  }

  // 지연 큐에 보존 중인 메일 중 현재 필터를 통과하지 못하는(읽힌) 메일을
  // 실제로 리스트에서 제거하고 큐를 비운다.
  // 사용자가 메일함을 떠나거나 새로고침하는 명시적 시점에만 호출한다. (OFM-210)
  flushDelayedData = (): void => {
    const delayedIds = Object.keys(this.delayedDataMap)
    if (delayedIds.length === 0) {
      return
    }

    const resultFilter = this.getResultFilter()
    const notiInfo = {}

    delayedIds.forEach((emailId) => {
      const emailObj = this.delayedDataMap[emailId]
      if (resultFilter(emailObj.data) !== true) {
        const removed = this.removeEmailData(emailObj.data, notiInfo)
        if (removed != null) {
          this.dataMgr.addEvent0Obj(
            notiInfo,
            TYPE.EMAIL,
            ACTION.DELETE,
            { type: TYPE.MAILBOX, id: this.getId() },
            removed,
          )
        }
      }
    })

    this.clearDelayedData()
    this.sortEmail()
    this.sortThread()

    const eventList = this.dataMgr.makeEvent0(notiInfo)
    const notiEvent = eventList.filter((item) => {
      return item.type === TYPE.THREAD || item.type === TYPE.EMAIL
    })
    this.dataMgr.notifyThreadChanged(notiEvent)
    this.dataMgr.notifyRelatedMailboxes(notiEvent)
  }

  isMoveEnabled = () => {
    return false
  }
}

export class UnreadMailbox extends FilterMailbox {
  constructor(dataMgr: any, options: any) {
    super(dataMgr, options)
    this.useUnreadCnt = true
  }

  getViewCountInfo = (): { count: number | null; type: string } | null => {
    return {
      count: this.data.unreadEmails,
      type: 'unread',
    }
  }

  notifyCountChanged = (count: any): void => {
    if (this.prevCount !== count) {
      this.prevCount = count
      this.dataMgr.unreadCount = count
      reduxStore.dispatch({ type: 'menu/RELOAD' })
    }
  }

  updateData = async (): Promise<any> => {
    let filters = this.getJmapFilter()

    const inMailboxOtherThan = filters.inMailboxOtherThan || []
    const mailboxes = (dataManager.mailbox.allList || []).filter((mailbox: any) => {
      return mailbox instanceof JMAPMailbox && !inMailboxOtherThan.includes(mailbox.getId())
    })
    let count = 0
    mailboxes.forEach((mailbox: any) => {
      count += mailbox.data.unreadEmails
    })

    this.notifyCountChanged(count)
    if (count != null) {
      // unreadMailbox는 total과 unreadEmails는 동일하므로 동일하게 처리.
      if (this.data.unreadEmails != count) {
        this.data.unreadEmails = count
        this.data.totalEmails = count
        this.dataMgr.mailbox.changeListener.notify({
          action: ACTION.UPDATE,
          type: TYPE.MAILBOX,
          data: { item: this },
        })
      }
    }
    return count
  }
}

// SearchMailbox 구현 (JMAPMailbox 상속, options 파라미터 추가)
// innerMailbox가 null인 경우는 모든 메일을 포함하는 것으로 처리.
export class SearchMailbox extends JMAPMailbox {
  options: any
  innerMailbox: JMAPMailbox | null | undefined
  searchMode: boolean
  // 검색 세션에서 명시적으로 이동(휴지통/보관함 등) 또는 완전삭제된 메일 ID 집합.
  // 이 메일들은 검색 결과에 존재하지 않는 것으로 간주한다 — 서버 검색 인덱스에 변경이
  // 반영되기 전의 재검색 응답이 목록에 되살리지 못하게 getResultFilter에서 제외하고,
  // next position도 이들을 뺀 가시 항목 수로 재계산한다. (OFM-225)
  movedEmailIds: Set<string>
  constructor(dataMgr: any, options: any) {
    super(dataMgr)
    this.options = options
    this.innerMailbox = null
    this.isRealMailbox = true
    this.searchMode = false
    this.movedEmailIds = new Set()
  }

  markMovedEmails = (emailIds: string[]): void => {
    emailIds.forEach((emailId) => {
      this.movedEmailIds.add(emailId)
    })
  }

  unmarkMovedEmails = (emailIds: string[]): void => {
    emailIds.forEach((emailId) => {
      this.movedEmailIds.delete(emailId)
    })
  }

  clearMovedEmails = (): void => {
    this.movedEmailIds.clear()
  }

  isMovedEmail = (emailId: string): boolean => {
    return this.movedEmailIds.has(emailId)
  }

  // 이동/완전삭제된 메일을 movedEmailIds에 등록하고 검색 목록에서 즉시 제거한다.
  // 목록 제거를 재검색 응답의 dedupe(stale 응답에 해당 메일이 있을 때만 동작)에 맡기면
  // 인덱스가 이미 갱신된 경우 메일이 목록에 잔존한다. 또 전체 검색은 getId()가 ''라
  // mailboxIds 기반 이벤트 target과 매칭되지 않으므로, searchMailbox 자신을 target으로
  // DELETE 이벤트를 직접 발행해 ThreadList가 목록 갱신·재조회하게 한다.
  applyMovedEmails = (emailIds: string[], notiInfo: any): void => {
    this.markMovedEmails(emailIds)
    emailIds.forEach((emailId) => {
      const email = this.getEmailById(emailId)
      if (email == null) {
        return
      }
      this.removeEmail_no_noti(email)
      this.removeEmailForEmailType(emailId)
      if (notiInfo != null) {
        this.dataMgr.addEvent0Obj(
          notiInfo,
          TYPE.EMAIL,
          ACTION.DELETE,
          { type: TYPE.MAILBOX, id: this.getId() },
          email,
        )
      }
    })
  }

  // OFM-225: 검색 모드는 이동/삭제로 서버 검색 목록이 당겨질 수 있어, next position을
  // 가시 항목 수(getResultFilter 통과 = movedEmailIds 제외)로 재계산한다.
  useLocalPositionCalc = (): boolean => {
    return this.searchMode === true || this.isRealMailbox !== true
  }

  // getReqState = (): number => {
  //   if (this.innerMailbox != null) {
  //     return this.innerMailbox.getReqState()
  //   }
  //   return this.position;
  // }

  setSearchMode = (searchMode: boolean): void => {
    if (this.searchMode !== searchMode) {
      // 검색 세션 경계(진입/종료)에서 이동 메일 집합 초기화.
      this.clearMovedEmails()
      // OMD-21: 매치 emailId 누적 집합도 초기화(THREAD position 계산용).
      this.searchMatchedIds.clear()
    }
    this.searchMode = searchMode
  }
  isSearchMode = (): boolean => {
    return this.searchMode === true
  }
  setInnerMailbox = (mailbox: JMAPMailbox): void => {
    this.innerMailbox = mailbox
    this.isEmailType = this.getListType() === TYPE.EMAIL
  }
  getInnerMailbox = (): any => {
    return this.innerMailbox
  }

  isContain = (email: any): boolean => {
    if (this.innerMailbox != null) {
      return this.innerMailbox.isContain(email)
    }
    return true
  }

  getViewCountInfo = (): { count: number | null; type: string } | null => {
    if (this.innerMailbox != null) {
      return this.innerMailbox.getViewCountInfo()
    }
    return null
  }

  isfolderNameVisible = (emailObj: any): boolean => {
    if (this.innerMailbox != null) {
      return this.innerMailbox.isfolderNameVisible(emailObj)
    }
    return true
  }

  getThreadEmailfilter = (): any => {
    if (this.innerMailbox != null) {
      return this.innerMailbox.getThreadEmailfilter()
    }
    const threadEmailfilter = (emailObj: any) => {
      if (emailObj == null || emailObj.isMailState(EMAIL_STATE.NORMAL) !== true) {
        return false
      }
      return true
    }
    return threadEmailfilter
  }
  getId = (): any => {
    if (this.innerMailbox != null) {
      return this.innerMailbox.getId()
    }
    return ''
  }
  getDisplayName = (): any => {
    if (this.innerMailbox != null) {
      return this.innerMailbox.getDisplayName()
    }
    return i18n.t(`mailbox.folderMenu.allFolders`)
  }
  getRole = (): any => {
    if (this.innerMailbox != null) {
      return this.innerMailbox.getRole()
    }
    return 'all'
  }
  getJmapFilter = (): any => {
    if (this.innerMailbox != null) {
      return this.innerMailbox.getJmapFilter()
    }

    const trashMailbox = this.dataMgr.getMailboxForRole('trash')
    const junkMailbox = this.dataMgr.getMailboxForRole('junk')
    const draftMailbox = this.dataMgr.getMailboxForRole('drafts')

    const filter = {
      inMailboxOtherThan: [trashMailbox.getId(), junkMailbox.getId(), draftMailbox.getId()],
    }
    return filter as any
  }
  getResultFilter = (): any => {
    // OFM-212: OpenSearch 검색은 folderIds로 서버측에서 이미 결과를 스코프한다.
    // 클라 mailboxIds 필터는 중복이며, IMAP경로↔UUID 매핑 형식이 어긋나면(빈 mailboxIds)
    // Email 뷰에서 정상 hit을 전부 탈락시킬 수 있으므로 검색 모드에서는 필터를 건너뛴다.
    if (this.searchMode === true && emailSearchProvider === 'OPEN_SEARCH') {
      // OFM-225: 단 명시적으로 이동/완전삭제된 메일은 존재하지 않는 것으로 간주해 제외 —
      // 인덱스 반영 전 재검색 응답이 해당 메일을 목록에 되살리는 것을 막는다.
      return (emailData: { id: string }) => this.isMovedEmail(emailData.id) !== true
    }
    if (this.innerMailbox != null) {
      return this.innerMailbox.getResultFilter()
    }

    const mailboxIds = []
    const junkMailbox = this.dataMgr.getMailboxForRole('junk')
    if (junkMailbox != null) {
      mailboxIds.push(junkMailbox.getId())
    }
    const trashMailbox = this.dataMgr.getMailboxForRole('trash')
    if (trashMailbox != null) {
      mailboxIds.push(trashMailbox.getId())
    }
    const draftMailbox = this.dataMgr.getMailboxForRole('drafts')
    if (draftMailbox != null) {
      mailboxIds.push(draftMailbox.getId())
    }

    const filterFunc = (emailData: any) => {
      const isInvalidMailbox = mailboxIds.some((id) => emailData.mailboxIds[id] === true)
      return isInvalidMailbox !== true
    }
    return filterFunc
  }
  getListType = (): any => {
    // OFM-212 / OMD-21: OpenSearch 검색 결과의 표시 단위는 vars.searchResultDisplayType 로 결정.
    // 기본 'EMAIL'(개별 메일). 'THREAD' 로 전환하면 검색 결과를 스레드 묶음으로 표시한다.
    if (this.searchMode === true && emailSearchProvider === 'OPEN_SEARCH') {
      return searchResultDisplayType === 'THREAD' ? TYPE.THREAD : TYPE.EMAIL
    }
    if (this.innerMailbox != null) {
      return this.innerMailbox.getListType()
    }
    const isThread = settings.getValue('email').mailUseThread
    return isThread !== true ? TYPE.EMAIL : TYPE.THREAD
  }

  isMoveEnabled = () => {
    if (this.innerMailbox != null) {
      return this.innerMailbox.isMoveEnabled()
    }
    return true
  }

  // setSortInfo = (sortInfo: any) => {
  //   if (this.innerMailbox != null) {
  //     this.innerMailbox.setSortInfo(sortInfo)
  //   }
  //   this.sortInfo = sortInfo
  //   this.sortFunc = createSortFunc(sortInfo);
  // }

  // getSortFunc: () => any = () => {
  //   if (this.innerMailbox != null) {
  //     return this.innerMailbox.getSortFunc()
  //   }
  //   return this.sortFunc
  // }

  // getLimitEmail: () => EmailObj | null = () => {
  //   if (this.innerMailbox != null) {
  //     return this.innerMailbox.getLimitEmail()
  //   }
  //   if (this.limitEmailId != null) {
  //     return this.getEmailById(this.limitEmailId)
  //   }
  //   return null
  // }
}

// EmailForm 데이터를 EmailObj 형식의 헤더로 변환
const buildHeaders = (threading?: ThreadingFields | null, replyTo?: ReplyToAddress[] | null) => {
  const headers = []

  // Message-ID 생성
  const messageId = `<${Date.now()}.${Math.random().toString(36).substr(2, 9)}@officemail.io>`
  headers.push({ name: 'Message-ID', value: messageId })

  // Date 헤더
  headers.push({ name: 'Date', value: new Date().toUTCString() })

  // MIME-Version
  headers.push({ name: 'MIME-Version', value: '1.0' })

  // 회신 스레딩 헤더 — 이상값이면 In-Reply-To/References 둘 다 생략(malformed 방어는 buildThreadingHeaders 내부).
  headers.push(...buildThreadingHeaders(threading))

  // Reply-To 헤더(account.replyTo). 값이 없으면 미생성.
  headers.push(...buildReplyToHeaders(replyTo))

  // 기타 필요한 헤더들 추가 가능
  return headers
}

// EmailForm 데이터를 EmailObj 형식의 bodyPart로 변환
const buildBodyPart = (emailData: any) => {
  const bodyPart: any = {}

  bodyPart.hasHTMLBody = () => {
    return emailData.htmlBody != null && emailData.htmlBody.length > 0
  }

  bodyPart.getHTMLBody = () => {
    return emailData.htmlBody || ''
  }

  bodyPart.hasTextBody = () => {
    return emailData.htmlText != null && emailData.htmlText.length > 0
  }

  bodyPart.getTextBody = () => {
    // HTML에서 텍스트로 변환된 버전 사용
    return emailData.htmlText || ''
  }

  bodyPart.isCalendar = () => {
    return false
  }

  bodyPart.getCalendarBody = () => {
    return ''
  }

  return bodyPart
}

// EmailObj를 사용하여 EML 생성하는 헬퍼 메서드
export async function generateEMLUsingEmailObj(
  emailData: any,
  exceptBCC = false,
  threading: ThreadingFields | null = null,
) {
  try {
    console.log('=== generateEMLUsingEmailObj DEBUG ===')
    console.log('Input emailData:', emailData)
    console.log('Input emailData.attachments:', emailData.attachments)

    // 임시 EmailObj 인스턴스 생성
    const tempEmailObj = new JMAPEmail(dataManager, 'temp-schedule-send')

    // EmailForm의 데이터를 EmailObj에서 요구하는 형식으로 변환
    const detail = {
      from: emailData.from || [],
      to: emailData.to || [],
      cc: emailData.cc || [],
      // exceptBCC 가 true 이면 숨은 참조(bcc)를 EML 에서 제외한다.
      // EML 은 수신자에게 노출되는 내용이므로, bcc 는 서버에 별도 전달하고 EML 에서는 뺀다.
      ...(exceptBCC ? {} : { bcc: emailData.bcc || [] }),
      subject: emailData.subject || '',
      headers: buildHeaders(threading, emailData.replyTo),
      bodyPart: buildBodyPart(emailData),
    }

    console.log('Created detail:', detail)

    // detail을 임시로 설정
    tempEmailObj.detail = detail

    // 첨부파일 리스트 준비
    const attachmentList = emailData.attachments || []
    console.log('Prepared attachmentList:', attachmentList)

    // EmailObj의 buildEML 메서드 호출
    console.log('Calling tempEmailObj.buildEML with attachmentList')
    const result = await tempEmailObj.buildEML(attachmentList)
    console.log('buildEML result:', result)

    if (result.error) {
      const { message } = result.error as Error
      throw new Error(`EML generation failed: ${message}`)
    }

    console.log('=== END generateEMLUsingEmailObj DEBUG ===')
    return result.eml
  } catch (error) {
    console.error('EML generation using EmailObj failed:', error)
    throw error
  }
}

export function validateEMLContent(emlContent: string, emailData: any) {
  const errors = []
  const warnings = []

  try {
    // 기본 구조 검증
    if (!emlContent || typeof emlContent !== 'string') {
      errors.push('EML content is empty or invalid')
      return { errors, warnings, isValid: false }
    }

    // 헤더와 본문 분리
    const headerBodySplit = emlContent.split('\r\n\r\n')
    if (headerBodySplit.length < 2) {
      errors.push('Invalid EML structure: No header-body separator found')
      return { errors, warnings, isValid: false }
    }

    const headers = headerBodySplit[0]
    const body = headerBodySplit.slice(1).join('\r\n\r\n')

    // 필수 헤더 검증
    const requiredHeaders = ['From', 'Subject', 'Date', 'Message-ID', 'MIME-Version']
    requiredHeaders.forEach((header) => {
      const regex = new RegExp(`^${header}:`, 'im')
      if (!regex.test(headers)) {
        errors.push(`Missing required header: ${header}`)
      }
    })

    // To/CC/BCC 중 하나는 있어야 함
    const recipientHeaders = ['To', 'Cc', 'Bcc']
    const hasRecipient = recipientHeaders.some((header) => {
      const regex = new RegExp(`^${header}:`, 'im')
      return regex.test(headers)
    })
    if (!hasRecipient) {
      errors.push('No recipient headers found (To, Cc, or Bcc)')
    }

    // Content-Type 검증
    if (!headers.includes('Content-Type:')) {
      warnings.push('Content-Type header is missing')
    }

    // 첨부파일이 있는 경우 multipart 구조 검증
    if (emailData.attachments && emailData.attachments.length > 0) {
      if (!headers.includes('multipart/mixed') && !headers.includes('multipart/related')) {
        warnings.push(
          'Attachments present but Content-Type is not multipart/mixed or multipart/related',
        )
      }

      // boundary 검증
      const boundaryMatch = headers.match(/boundary="([^"]+)"/i)
      if (boundaryMatch) {
        const boundary = boundaryMatch[1]
        if (!body.includes(`--${boundary}`)) {
          errors.push('Boundary defined in header but not found in body')
        }
        if (!body.includes(`--${boundary}--`)) {
          warnings.push('Missing multipart closing boundary')
        }
      } else {
        errors.push('Multipart message but no boundary defined')
      }
    }

    // 줄 길이 검증 (RFC 2822: 78자 권장, 998자 최대)
    const lines = emlContent.split('\r\n')
    lines.forEach((line, index) => {
      if (line.length > 998) {
        errors.push(`Line ${index + 1} exceeds maximum length (998 characters)`)
      } else if (line.length > 78 && !line.includes('=')) {
        // quoted-printable 제외
        warnings.push(`Line ${index + 1} exceeds recommended length (78 characters)`)
      }
    })

    // Message-ID 형식 검증
    const messageIdMatch = headers.match(/Message-ID:\s*<([^>]+)>/i)
    if (messageIdMatch) {
      const messageId = messageIdMatch[1]
      if (!messageId.includes('@')) {
        warnings.push('Message-ID should contain @ symbol')
      }
    }

    // Date 형식 검증
    const dateMatch = headers.match(/Date:\s*(.+)/i)
    if (dateMatch) {
      const dateStr = dateMatch[1].trim()
      const date = new Date(dateStr)
      if (isNaN(date.getTime())) {
        warnings.push('Date header has invalid format')
      }
    }

    const isValid = errors.length === 0
    return { errors, warnings, isValid }
  } catch (error: any) {
    errors.push(`Validation error: ${error.message}`)
    return { errors, warnings, isValid: false }
  }
}

export function getEmailIdsByMessageId(messageId: any) {
  return requestEmailIdsByMessageId(messageId, null)
}

// OMD-222: retryOnTimeout=true인 경우에만 60초 hard timeout(AbortError) 시 1회 재시도한다.
// 재시도는 최악의 대기 시간을 60초→120초로 늘리므로, 딥링크 이동(moveByMessageId)처럼
// 재시도 이득이 명확한 호출부만 opt-in한다. (ai-agent/quick-agent 뷰어는 기존 동작 유지)
export function getThreadByMessageId(messageId: any, retryOnTimeout: boolean = false) {
  return new Promise((resolve) => {
    const msgId = messageId.replace(/[<>]/g, '')
    const request = (): Promise<{
      emailIds: any
      threadList: any
      allEmailList: any
      info: any
    }> => requestThreadByMessageId(msgId, null)
    ;(retryOnTimeout ? requestWithAbortRetry(request) : request())
      .then(({ emailIds, threadList, allEmailList, /* total, */ info }) => {
        console.log(
          ' -- requestThreadByMessageId : ',
          emailIds,
          threadList,
          allEmailList,
          // total,
          info,
        )

        const notiInfo = null

        const emailObjList = []
        let threadObj = null
        let titleEmail = null

        if (allEmailList != null) {
          allEmailList.forEach((emailData0) => {
            let emailData = emailData0
            if (emailData.messageId == null) {
              const fakeEmailData = {
                id: emailData.id,
                threadId: emailData.threadId,
                mailboxIds: emailData.mailboxIds || {},
                keywords: emailData.keywords,
                extra: {
                  isFake: true,
                  mailboxObj: null,
                },
              }
              if (threadList != null) {
                const threadData = threadList.find((threadData) => {
                  return threadData.emailIds.indexOf(emailData.id) > -1
                })
                fakeEmailData.threadId = threadData.id
              }
              emailData = fakeEmailData
            }

            const email = dataManager.applyEmailData(emailData, notiInfo, false, null)
            if (email != null) {
              if (email.data.id === emailIds[0]) {
                titleEmail = email
              }
              const thread = dataManager.getThreadById(email.data.threadId)
              thread.addEmail(email, notiInfo, true, null)
              emailObjList.push(email)
            }
          })
        }
        if (threadList != null) {
          threadList.forEach((threadData) => {
            const thread = dataManager.applyThreadData(threadData, notiInfo, false, null)
            threadObj = thread
            return thread
          })
        }

        emailObjList.forEach((emailObj) => {
          emailObj.processMailbox()
        })
        // this.notifyChangeAll()

        resolve({ emailIds, threadObj, emailObjList, titleEmail /* total */ })
      })
      .catch((err) => {
        console.log(' -- requestThreadByMessageId error : ', err)
        resolve(null)
      })
  })
}

const retMap = {
  email: JMAPEmail,
  mailbox: JMAPMailbox,
  filtered: FilterMailbox,
  search: SearchMailbox,
}

export default retMap
